import { DefaultTemplate, TemplateCampo, COR, CORES_SIM_NAO, CORES_PRIORIDADE } from './types';

// ============================================================
// ARRECADAÇÃO E TRIBUTOS (+ Balcão do Prefeito)
//
// O balcão da Fazenda municipal em quadros: IPTU, ITBI, ISS, NFS-e, dívida
// ativa, certidões, restituição, isenções e alvará. Os prazos que importam
// saem do CTN e de lei federal, não de meta interna:
//   - Certidão: 10 dias da entrada do pedido (CTN art. 205, parágrafo único)
//   - Restituição: 5 anos do pagamento indevido (CTN art. 168)
//   - ITBI: base é o valor declarado; o fisco só afasta com processo próprio
//     e contraditório (CTN art. 148; STJ Tema 1113)
//   - Baixo risco dispensa ato público de liberação (Lei 13.874/2019)
// SLAs em horas corridas (dias × 24), padrão dos demais templates.
//
// Todo template abre com o mesmo bloco de identificação do contribuinte
// (camposContribuinte): CPF/CNPJ, nome, WhatsApp, e-mail, inscrição quando
// couber, canal de entrada, protocolo, bairro e aceite LGPD. Os cards de
// exemplo são do "Município Exemplo" — nomes e documentos fictícios.
// ============================================================

const CANAIS_ENTRADA = ['Portal', 'WhatsApp', 'Balcão', 'Telefone'];
const CORES_CANAL: Record<string, string> = {
  'Portal': COR.azul,
  'WhatsApp': COR.verde,
  'Balcão': COR.amarelo,
  'Telefone': COR.cinza,
};
const ACEITE_LGPD = 'Autorizo o uso dos meus dados para este atendimento (LGPD)';

type TipoInscricao = 'imobiliaria' | 'mobiliaria' | 'ambas' | 'nenhuma';

const NOME_INSCRICAO: Record<Exclude<TipoInscricao, 'nenhuma'>, string> = {
  imobiliaria: 'Inscrição Imobiliária',
  mobiliaria: 'Inscrição Mobiliária (CCM)',
  ambas: 'Inscrição Imobiliária / Mobiliária',
};

/**
 * Bloco de identificação comum a todos os templates deste arquivo.
 * IDs: <p>_doc, <p>_nome, <p>_whats, <p>_email, <p>_insc, <p>_canal,
 * <p>_prot, <p>_bairro, <p>_lgpd — locais ao template, remapeados na criação.
 */
function camposContribuinte(
  p: string,
  inscricao: TipoInscricao,
  opts: { rotuloNome?: string; inscricaoObrigatoria?: boolean } = {},
): TemplateCampo[] {
  const campos: TemplateCampo[] = [
    { id: `${p}_doc`, nome: 'CPF / CNPJ', tipo: 'texto', obrigatorio: true },
    { id: `${p}_nome`, nome: opts.rotuloNome || 'Nome / Razão Social', tipo: 'texto', obrigatorio: true },
    { id: `${p}_whats`, nome: 'WhatsApp', tipo: 'telefone', obrigatorio: false },
    { id: `${p}_email`, nome: 'E-mail', tipo: 'texto', obrigatorio: false },
  ];
  if (inscricao !== 'nenhuma') {
    campos.push({ id: `${p}_insc`, nome: NOME_INSCRICAO[inscricao], tipo: 'texto', obrigatorio: !!opts.inscricaoObrigatoria });
  }
  campos.push(
    { id: `${p}_canal`, nome: 'Canal de Entrada', tipo: 'lista', obrigatorio: true, opcoes: [...CANAIS_ENTRADA], coresOpcoes: { ...CORES_CANAL } },
    { id: `${p}_prot`, nome: 'Nº do Protocolo', tipo: 'texto', obrigatorio: false },
    { id: `${p}_bairro`, nome: 'Bairro', tipo: 'texto', obrigatorio: false },
    { id: `${p}_lgpd`, nome: 'Aceite LGPD', tipo: 'checkbox_single', obrigatorio: true, opcoes: [ACEITE_LGPD] },
  );
  return campos;
}

/** Valores do bloco de identificação para os cards de exemplo (aceite LGPD já marcado). */
function dadosContribuinte(
  p: string,
  d: { doc: string; nome: string; whats?: string; email?: string; insc?: string; canal: string; prot: string; bairro?: string },
): Record<string, any> {
  const v: Record<string, any> = {
    [`${p}_doc`]: d.doc,
    [`${p}_nome`]: d.nome,
    [`${p}_canal`]: d.canal,
    [`${p}_prot`]: d.prot,
    [`${p}_lgpd`]: ACEITE_LGPD,
  };
  if (d.whats) v[`${p}_whats`] = d.whats;
  if (d.email) v[`${p}_email`] = d.email;
  if (d.insc) v[`${p}_insc`] = d.insc;
  if (d.bairro) v[`${p}_bairro`] = d.bairro;
  return v;
}

const SIM_NAO = ['Sim', 'Não'];
const CORES_DECISAO: Record<string, string> = {
  'Deferido': COR.verde,
  'Deferido em parte': COR.amarelo,
  'Indeferido': COR.vermelho,
};

// ============================================================
// A1 — IPTU: revisão e impugnação
// ============================================================
const iptuRevisao: DefaultTemplate = {
  id: 'tpl_iptu_revisao',
  name: 'IPTU: Revisão e Impugnação',
  descricao: 'Pedido de revisão de valor venal, área ou padrão e impugnação do lançamento: triagem documental, vistoria e conferência do cadastro, parecer fiscal, validação da chefia quando a diferença passa de 20%, decisão, ciência e recurso.',
  categoria: 'arrecadacao',
  icone: '🏠',
  etiquetas: [
    { nome: 'Diferença acima de 20%', cor: COR.vermelho },
    { nome: 'Prioridade Legal (idoso/PCD)', cor: COR.roxo },
    { nome: 'Grande Contribuinte', cor: COR.azul },
    { nome: 'Prazo de Impugnação', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'iptu_e1', nome: 'Entrada',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_insc', 'iptu_c2'],
      permiteMoverPara: ['iptu_e2'],
      campos: [
        { id: 'iptu_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('iptu', 'imobiliaria', { inscricaoObrigatoria: true }),
        { id: 'iptu_c2', nome: 'Tipo de Pedido', tipo: 'lista', obrigatorio: true, opcoes: ['Revisão de valor venal', 'Impugnação de lançamento', 'Erro de área construída', 'Erro de padrão / categoria', 'Erro de titularidade', 'Revisão de uso / alíquota'], coresOpcoes: { 'Impugnação de lançamento': COR.vermelho } },
        { id: 'iptu_c3', nome: 'Exercício', tipo: 'numero', obrigatorio: true, valorMinimo: 2000, valorMaximo: 2100, precisao: 0 },
        { id: 'iptu_c4', nome: 'Valor Lançado', tipo: 'valor', obrigatorio: false },
        { id: 'iptu_c5', nome: 'Valor que o Contribuinte Entende Correto', tipo: 'valor', obrigatorio: false },
        { id: 'iptu_c6', nome: 'Fundamentação do Contribuinte', tipo: 'texto_longo', obrigatorio: true },
        { id: 'iptu_c7', nome: 'Documentos (matrícula, fotos, laudo)', tipo: 'anexo', obrigatorio: false },
        { id: 'iptu_c8', nome: 'Prazo de Impugnação', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
    {
      id: 'iptu_e2', nome: 'Triagem',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_insc', 'iptu_c9'],
      permiteMoverPara: ['iptu_e3', 'iptu_e4', 'iptu_e10'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['iptu_c9'],
      campos: [
        { id: 'iptu_c9', nome: 'Documentação completa?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'iptu_c10', nome: 'Documentos Faltantes', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Matrícula / escritura', 'Documento do requerente', 'Procuração', 'Fotos do imóvel', 'Laudo de avaliação', 'Planta / croqui'] },
        { id: 'iptu_c11', nome: 'Pedido dentro do prazo?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iptu_e3', nome: 'Pendência do Contribuinte',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_whats', 'iptu_c13'],
      permiteMoverPara: ['iptu_e2', 'iptu_e10'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['iptu_c15'],
      campos: [
        { id: 'iptu_c12', nome: 'Exigência Enviada', tipo: 'texto_longo', obrigatorio: true },
        { id: 'iptu_c13', nome: 'Prazo para Atender', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'iptu_c14', nome: 'Documentos Entregues', tipo: 'anexo', obrigatorio: false },
        { id: 'iptu_c15', nome: 'Exigência atendida?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iptu_e4', nome: 'Vistoria / Cadastro',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_bairro', 'iptu_c17'],
      permiteMoverPara: ['iptu_e5', 'iptu_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'iptu_c16', nome: 'Precisa de vistoria no local?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'iptu_c17', nome: 'Fiscal / Cadastrador', tipo: 'texto', obrigatorio: false },
        { id: 'iptu_c18', nome: 'Data da Vistoria', tipo: 'data', obrigatorio: false },
        { id: 'iptu_c19', nome: 'Área Construída Apurada (m²)', tipo: 'numero', obrigatorio: false },
        { id: 'iptu_c20', nome: 'Divergência Cadastral Encontrada', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iptu_c21', nome: 'Laudo / Fotos da Vistoria', tipo: 'anexo', obrigatorio: false },
        { id: 'iptu_c22', nome: 'Cadastro conferido?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iptu_e5', nome: 'Parecer Fiscal',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_c24', 'iptu_c25'],
      permiteMoverPara: ['iptu_e6', 'iptu_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'iptu_c23', nome: 'Valor Venal Revisto', tipo: 'valor', obrigatorio: false },
        { id: 'iptu_c24', nome: 'Variação sobre o Lançado (%, em módulo)', tipo: 'numero', obrigatorio: true, valorMinimo: 0, precisao: 0 },
        { id: 'iptu_c25', nome: 'Parecer', tipo: 'lista', obrigatorio: true, opcoes: ['Procedente', 'Procedente em parte', 'Improcedente'], coresOpcoes: { 'Procedente': COR.verde, 'Procedente em parte': COR.amarelo, 'Improcedente': COR.vermelho } },
        { id: 'iptu_c26', nome: 'Fundamentação do Parecer', tipo: 'texto_longo', obrigatorio: true },
        { id: 'iptu_c27', nome: 'Auditor Fiscal', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'iptu_e6', nome: 'Validação da Chefia',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_c24', 'iptu_c28'],
      permiteMoverPara: ['iptu_e7', 'iptu_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'iptu_c28', nome: 'Chefia homologa o parecer?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'iptu_c29', nome: 'Observação da Chefia', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'iptu_e7', nome: 'Decisão',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_c30', 'iptu_c32'],
      permiteMoverPara: ['iptu_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'iptu_c30', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Deferido', 'Deferido em parte', 'Indeferido'], coresOpcoes: CORES_DECISAO },
        { id: 'iptu_c31', nome: 'Valor Venal Final', tipo: 'valor', obrigatorio: false },
        { id: 'iptu_c32', nome: 'Novo Valor do IPTU', tipo: 'valor', obrigatorio: false },
        { id: 'iptu_c33', nome: 'Autoridade Julgadora', tipo: 'texto', obrigatorio: false },
        { id: 'iptu_c34', nome: 'Decisão Assinada', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'iptu_e8', nome: 'Ciência',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_c30', 'iptu_c37'],
      permiteMoverPara: ['iptu_e9', 'iptu_e10'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'iptu_c35', nome: 'Forma da Ciência', tipo: 'lista', obrigatorio: false, opcoes: ['WhatsApp', 'E-mail', 'Portal', 'Pessoalmente', 'Correio com AR', 'Edital'] },
        { id: 'iptu_c36', nome: 'Data da Ciência', tipo: 'data', obrigatorio: false },
        { id: 'iptu_c37', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'iptu_c38', nome: 'Guia Recalculada', tipo: 'anexo', obrigatorio: false },
        { id: 'iptu_c39', nome: 'Houve recurso no prazo?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iptu_e9', nome: 'Recurso',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['iptu_c1', 'iptu_c41', 'iptu_c42'],
      permiteMoverPara: ['iptu_e10'],
      permiteCriarCards: false,
      campos: [
        { id: 'iptu_c40', nome: 'Razões do Recurso', tipo: 'texto_longo', obrigatorio: true },
        { id: 'iptu_c41', nome: 'Instância', tipo: 'lista', obrigatorio: false, opcoes: ['Junta de Recursos Fiscais', 'Conselho de Contribuintes', 'Secretário da Fazenda'] },
        { id: 'iptu_c42', nome: 'Resultado do Recurso', tipo: 'lista', obrigatorio: false, opcoes: ['Provido', 'Provido em parte', 'Não provido'], coresOpcoes: { 'Provido': COR.verde, 'Provido em parte': COR.amarelo, 'Não provido': COR.vermelho } },
        { id: 'iptu_c43', nome: 'Acórdão / Decisão do Recurso', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'iptu_e10', nome: 'Encerrado',
      camposVisiveisNoCard: ['iptu_c1', 'iptu_insc', 'iptu_c44'],
      permiteCriarCards: false,
      campos: [
        { id: 'iptu_c44', nome: 'Resultado Final', tipo: 'lista', obrigatorio: false, opcoes: ['Lançamento revisto', 'Lançamento mantido', 'Arquivado por falta de documento', 'Pedido fora do prazo', 'Desistência'], coresOpcoes: { 'Lançamento revisto': COR.verde, 'Lançamento mantido': COR.cinza, 'Arquivado por falta de documento': COR.amarelo } },
        { id: 'iptu_c45', nome: 'Cadastro imobiliário atualizado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'iptu_c46', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Documentação completa vai para vistoria; incompleta volta ao contribuinte',
      etapaOrigemId: 'iptu_e2',
      condicao: { campoId: 'iptu_c9', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iptu_e4',
      etapaDestinoSenaoId: 'iptu_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Lista de documentos faltantes só quando falta documento',
      etapaOrigemId: 'iptu_e2',
      condicao: { campoId: 'iptu_c9', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['iptu_c10'],
    },
    {
      tipo: 'mover',
      nome: 'Exigência atendida volta para a triagem',
      etapaOrigemId: 'iptu_e3',
      condicao: { campoId: 'iptu_c15', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iptu_e2',
    },
    {
      tipo: 'mover',
      nome: 'Cadastro conferido segue para o parecer fiscal',
      etapaOrigemId: 'iptu_e4',
      condicao: { campoId: 'iptu_c22', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iptu_e5',
    },
    {
      tipo: 'mover',
      nome: 'Variação acima de 20% vai à chefia',
      etapaOrigemId: 'iptu_e5',
      condicao: { campoId: 'iptu_c24', operador: 'maior', valor: 20 },
      etapaDestinoId: 'iptu_e6',
      etapaDestinoSenaoId: 'iptu_e7',
    },
    {
      tipo: 'mover',
      nome: 'Chefia homologa e libera a decisão; senão devolve ao fiscal',
      etapaOrigemId: 'iptu_e6',
      condicao: { campoId: 'iptu_c28', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iptu_e7',
      etapaDestinoSenaoId: 'iptu_e5',
    },
    {
      tipo: 'mover',
      nome: 'Decisão proferida vai para ciência',
      etapaOrigemId: 'iptu_e7',
      condicao: { campoId: 'iptu_c30', operador: 'preenchido' },
      etapaDestinoId: 'iptu_e8',
    },
    {
      tipo: 'mover',
      nome: 'Recurso protocolado abre a etapa de recurso',
      etapaOrigemId: 'iptu_e8',
      condicao: { campoId: 'iptu_c39', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iptu_e9',
    },
    {
      tipo: 'mover',
      nome: 'Recurso julgado encerra o processo',
      etapaOrigemId: 'iptu_e9',
      condicao: { campoId: 'iptu_c42', operador: 'preenchido' },
      etapaDestinoId: 'iptu_e10',
    },
  ],
  cards: [
    {
      id: 'iptu_card1', titulo: 'Revisão de área construída - Rua das Acácias, 120', etapaId: 'iptu_e4',
      valores: {
        iptu_c1: 'Revisão de área construída - Rua das Acácias, 120',
        ...dadosContribuinte('iptu', { doc: '***.418.207-**', nome: 'Helena Prado Exemplo', whats: '(00) 99999-0101', email: 'helena.exemplo@example.com', insc: '01.02.033.0120.001', canal: 'Portal', prot: '2026/IPTU-000412', bairro: 'Jardim das Flores' }),
        iptu_c2: 'Erro de área construída', iptu_c3: 2026, iptu_c4: 2480, iptu_c5: 1650, iptu_c9: 'Sim', iptu_c16: 'Sim', iptu_c17: 'Fiscal de cadastro 03',
        iptu_c6: 'Cadastro registra 210 m², mas a edícula foi demolida em 2024. Área real: 140 m².',
      },
    },
    {
      id: 'iptu_card2', titulo: 'Impugnação do valor venal - Sala comercial Centro', etapaId: 'iptu_e6', etiquetas: ['Diferença acima de 20%', 'Grande Contribuinte'],
      valores: {
        iptu_c1: 'Impugnação do valor venal - Sala comercial Centro',
        ...dadosContribuinte('iptu', { doc: '11.222.333/0001-81', nome: 'Empresa Exemplo Participações Ltda', email: 'fiscal@empresa-exemplo.example.com', insc: '01.01.004.0300.015', canal: 'Portal', prot: '2026/IPTU-000377', bairro: 'Centro' }),
        iptu_c2: 'Impugnação de lançamento', iptu_c3: 2026, iptu_c4: 18900, iptu_c5: 12400, iptu_c9: 'Sim', iptu_c22: 'Sim',
        iptu_c23: 1310000, iptu_c24: 28, iptu_c25: 'Procedente em parte', iptu_c26: 'Planta genérica superestimou o m² da quadra; laudo do contribuinte compatível com transações recentes.',
      },
    },
    {
      id: 'iptu_card3', titulo: 'Erro de padrão construtivo - casa térrea', etapaId: 'iptu_e3', etiquetas: ['Prioridade Legal (idoso/PCD)'],
      valores: {
        iptu_c1: 'Erro de padrão construtivo - casa térrea',
        ...dadosContribuinte('iptu', { doc: '***.902.115-**', nome: 'Joaquim Exemplo dos Santos', whats: '(00) 99999-0102', insc: '03.07.011.0045.001', canal: 'Balcão', prot: '2026/IPTU-000430', bairro: 'Vila Nova' }),
        iptu_c2: 'Erro de padrão / categoria', iptu_c3: 2026, iptu_c9: 'Não', iptu_c10: ['Matrícula / escritura', 'Fotos do imóvel'],
        iptu_c12: 'Enviar matrícula atualizada e fotos da fachada e dos fundos para conferência do padrão.',
      },
    },
    {
      id: 'iptu_card4', titulo: 'Revisão de valor venal - terreno baldio', etapaId: 'iptu_e8',
      valores: {
        iptu_c1: 'Revisão de valor venal - terreno baldio',
        ...dadosContribuinte('iptu', { doc: '***.330.604-**', nome: 'Carla Exemplo Moura', whats: '(00) 99999-0103', insc: '05.02.019.0007.000', canal: 'WhatsApp', prot: '2026/IPTU-000298', bairro: 'Bairro Industrial' }),
        iptu_c2: 'Revisão de valor venal', iptu_c3: 2026, iptu_c24: 8, iptu_c25: 'Procedente', iptu_c30: 'Deferido', iptu_c32: 610, iptu_c35: 'WhatsApp',
      },
    },
    {
      id: 'iptu_card5', titulo: 'Titularidade errada no carnê', etapaId: 'iptu_e1', etiquetas: ['Prazo de Impugnação'],
      valores: {
        iptu_c1: 'Titularidade errada no carnê',
        ...dadosContribuinte('iptu', { doc: '***.771.208-**', nome: 'Rafael Exemplo Lima', whats: '(00) 99999-0104', insc: '02.04.022.0110.002', canal: 'Telefone', prot: '2026/IPTU-000451', bairro: 'Morada do Sol' }),
        iptu_c2: 'Erro de titularidade', iptu_c3: 2026,
      },
    },
  ],
  tour: [
    '🏠 Revisão e impugnação de IPTU com trilha completa: do pedido à guia recalculada.',
    '1️⃣ "Entrada": inscrição imobiliária, exercício e o valor que o contribuinte entende correto.',
    '2️⃣ "Triagem": faltou documento? o card volta sozinho para "Pendência do Contribuinte" com a lista do que falta.',
    '3️⃣ "Vistoria / Cadastro": área apurada e divergência cadastral registradas com foto.',
    '4️⃣ "Parecer Fiscal": a variação acima de 20% vai automaticamente para a validação da chefia.',
    '5️⃣ "Decisão" e "Ciência": decisão fundamentada, guia recalculada e prazo de recurso controlado.',
    '⚖️ "Recurso": instância e resultado registrados; julgado, o processo encerra.',
    '📌 Use a etiqueta "Prioridade Legal" para idoso e PCD — eles têm preferência na fila.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento da Fazenda / Portal', objetivo: 'Registrar o pedido com inscrição e exercício — sem inscrição não há como conferir o cadastro.' },
    { quemFaz: 'Setor de Cadastro Imobiliário', objetivo: 'Conferir os documentos antes de gastar vistoria.', automacoes: ['Já configurado: documentação incompleta volta ao contribuinte'] },
    { quemFaz: 'Atendimento / contribuinte', objetivo: 'Cobrar o documento faltante pelo WhatsApp e controlar o prazo.' },
    { quemFaz: 'Fiscal de cadastro', objetivo: 'Vistoriar quando preciso e atualizar área, padrão e uso.' },
    { quemFaz: 'Auditor fiscal', objetivo: 'Emitir parecer fundamentado e informar a variação sobre o lançado.', automacoes: ['Já configurado: variação acima de 20% vai à chefia'] },
    { quemFaz: 'Chefia da Fiscalização', objetivo: 'Homologar as revisões de maior impacto na arrecadação.' },
    { quemFaz: 'Autoridade julgadora de 1ª instância', objetivo: 'Decidir com fundamento — decisão sem motivação é nula.' },
    { quemFaz: 'Atendimento da Fazenda', objetivo: 'Dar ciência com a guia recalculada e informar o prazo de recurso.' },
    { quemFaz: 'Junta de Recursos / Conselho de Contribuintes', objetivo: 'Julgar o recurso e devolver o processo para encerramento.' },
    { quemFaz: 'Cadastro Imobiliário', objetivo: 'Garantir que o cadastro foi atualizado para o próximo lançamento.', conexoes: ['Ligue à "Certidão Negativa" quando o contribuinte precisar da certidão após a revisão'] },
  ],
};

// ============================================================
// A2 — ITBI: declaração, avaliação e guia
// ============================================================
const itbi: DefaultTemplate = {
  id: 'tpl_itbi',
  name: 'ITBI: Declaração, Avaliação e Guia',
  descricao: 'Do formulário do cartório à certidão de quitação: valor declarado compatível com a referência vai direto para a guia; fora da faixa abre avaliação com contraditório, como manda o STJ (Tema 1113) e o art. 148 do CTN.',
  categoria: 'arrecadacao',
  icone: '🔑',
  etiquetas: [
    { nome: 'Avaliação em Contraditório', cor: COR.amarelo },
    { nome: 'Financiamento SFH', cor: COR.azul },
    { nome: 'Imunidade / Não Incidência', cor: COR.roxo },
    { nome: 'Escritura Agendada', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'itbi_e1', nome: 'Declaração',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c3', 'itbi_c4'],
      permiteMoverPara: ['itbi_e2'],
      campos: [
        { id: 'itbi_c1', nome: 'Imóvel / Transação', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('itbi', 'imobiliaria', { rotuloNome: 'Adquirente (Nome / Razão Social)', inscricaoObrigatoria: true }),
        { id: 'itbi_c2', nome: 'Transmitente', tipo: 'texto', obrigatorio: true },
        { id: 'itbi_c3', nome: 'Natureza da Transação', tipo: 'lista', obrigatorio: true, opcoes: ['Compra e venda', 'Compra e venda financiada (SFH)', 'Dação em pagamento', 'Permuta', 'Arrematação em leilão', 'Cessão de direitos', 'Integralização de capital'], coresOpcoes: { 'Compra e venda financiada (SFH)': COR.azul, 'Integralização de capital': COR.roxo } },
        { id: 'itbi_c4', nome: 'Valor Declarado da Transação', tipo: 'valor', obrigatorio: true },
        { id: 'itbi_c5', nome: 'Valor Financiado (SFH)', tipo: 'valor', obrigatorio: false },
        { id: 'itbi_c6', nome: 'Matrícula do Imóvel', tipo: 'texto', obrigatorio: false },
        { id: 'itbi_c7', nome: 'Cartório / Tabelionato', tipo: 'texto', obrigatorio: false },
        { id: 'itbi_c8', nome: 'Contrato / Minuta da Escritura', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'itbi_e2', nome: 'Conferência',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c4', 'itbi_c10'],
      permiteMoverPara: ['itbi_e3', 'itbi_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'itbi_c9', nome: 'Valor de Referência do Cadastro', tipo: 'valor', obrigatorio: false },
        { id: 'itbi_c10', nome: 'Valor declarado dentro da faixa de referência?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'itbi_c11', nome: 'Base legal', tipo: 'texto_informativo', obrigatorio: false, textoInformativo: 'STJ, Tema 1113: a base do ITBI é o valor da transação declarado, que se presume verdadeiro. O valor de referência do município não pode ser imposto de forma unilateral — para afastar o declarado é preciso abrir avaliação com contraditório (CTN art. 148).' },
        { id: 'itbi_c12', nome: 'Documentação conferida?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'itbi_c13', nome: 'Observação da Conferência', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'itbi_e3', nome: 'Avaliação (contraditório)',
      tempoMaximoHoras: 360,
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c14', 'itbi_c18'],
      permiteMoverPara: ['itbi_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'itbi_c14', nome: 'Valor Apurado na Avaliação', tipo: 'valor', obrigatorio: false },
        { id: 'itbi_c15', nome: 'Fundamentação da Avaliação (CTN art. 148)', tipo: 'texto_longo', obrigatorio: true },
        { id: 'itbi_c16', nome: 'Laudo de Avaliação', tipo: 'anexo', obrigatorio: false },
        { id: 'itbi_c17', nome: 'Contribuinte notificado para contestar?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'itbi_c18', nome: 'Prazo de Contestação', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'itbi_c19', nome: 'Contestação do Contribuinte', tipo: 'anexo', obrigatorio: false },
        { id: 'itbi_c20', nome: 'Base de Cálculo Definida', tipo: 'lista', obrigatorio: false, opcoes: ['Valor declarado mantido', 'Valor arbitrado na avaliação'], coresOpcoes: { 'Valor declarado mantido': COR.verde, 'Valor arbitrado na avaliação': COR.amarelo } },
      ],
    },
    {
      id: 'itbi_e4', nome: 'Guia Emitida',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c23', 'itbi_c24'],
      permiteMoverPara: ['itbi_e5', 'itbi_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'itbi_c21', nome: 'Base de Cálculo', tipo: 'valor', obrigatorio: true },
        { id: 'itbi_c22', nome: 'Alíquota (%)', tipo: 'numero', obrigatorio: false },
        { id: 'itbi_c23', nome: 'Valor do ITBI', tipo: 'valor', obrigatorio: true },
        { id: 'itbi_c24', nome: 'Vencimento da Guia', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'itbi_c25', nome: 'Guia (DAM)', tipo: 'anexo', obrigatorio: false },
        { id: 'itbi_c26', nome: 'Pagamento confirmado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'itbi_e5', nome: 'Pago',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c28', 'itbi_c29'],
      permiteMoverPara: ['itbi_e6'],
      permiteCriarCards: false,
      campos: [
        { id: 'itbi_c27', nome: 'Data do Pagamento', tipo: 'data', obrigatorio: false },
        { id: 'itbi_c28', nome: 'Valor Pago', tipo: 'valor', obrigatorio: false },
        { id: 'itbi_c29', nome: 'Baixa conferida no sistema?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'itbi_e6', nome: 'Quitação',
      camposVisiveisNoCard: ['itbi_c1', 'itbi_c30', 'itbi_c32'],
      permiteCriarCards: false,
      campos: [
        { id: 'itbi_c30', nome: 'Nº da Certidão de Quitação', tipo: 'texto', obrigatorio: false },
        { id: 'itbi_c31', nome: 'Certidão de Quitação do ITBI', tipo: 'anexo', obrigatorio: false },
        { id: 'itbi_c32', nome: 'Enviada ao cartório?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'itbi_c33', nome: 'Titularidade transferida no cadastro?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Dentro da faixa vai direto para a guia; fora abre avaliação (Tema 1113)',
      etapaOrigemId: 'itbi_e2',
      condicao: { campoId: 'itbi_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'itbi_e4',
      etapaDestinoSenaoId: 'itbi_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Prazo e contestação só depois de notificar',
      etapaOrigemId: 'itbi_e3',
      condicao: { campoId: 'itbi_c17', operador: 'igual', valor: 'Sim' },
      camposAfetadosIds: ['itbi_c18', 'itbi_c19'],
    },
    {
      tipo: 'mover',
      nome: 'Base definida na avaliação libera a guia',
      etapaOrigemId: 'itbi_e3',
      condicao: { campoId: 'itbi_c20', operador: 'preenchido' },
      etapaDestinoId: 'itbi_e4',
    },
    {
      tipo: 'mover',
      nome: 'Pagamento confirmado move para Pago',
      etapaOrigemId: 'itbi_e4',
      condicao: { campoId: 'itbi_c26', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'itbi_e5',
    },
    {
      tipo: 'mover',
      nome: 'Baixa conferida emite a quitação',
      etapaOrigemId: 'itbi_e5',
      condicao: { campoId: 'itbi_c29', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'itbi_e6',
    },
  ],
  cards: [
    {
      id: 'itbi_card1', titulo: 'Apartamento 302 - Ed. Exemplo', etapaId: 'itbi_e4', etiquetas: ['Financiamento SFH'],
      valores: {
        itbi_c1: 'Apartamento 302 - Ed. Exemplo',
        ...dadosContribuinte('itbi', { doc: '***.205.884-**', nome: 'Bruno Exemplo Teixeira', whats: '(00) 99999-0201', email: 'bruno.exemplo@example.com', insc: '01.03.010.0200.302', canal: 'Portal', prot: '2026/ITBI-001208', bairro: 'Centro' }),
        itbi_c2: 'Construtora Exemplo Ltda', itbi_c3: 'Compra e venda financiada (SFH)', itbi_c4: 420000, itbi_c5: 336000, itbi_c7: '1º Tabelionato de Notas',
        itbi_c9: 405000, itbi_c10: 'Sim', itbi_c21: 420000, itbi_c22: 2, itbi_c23: 8400,
      },
    },
    {
      id: 'itbi_card2', titulo: 'Terreno 14 - Loteamento Morada do Sol', etapaId: 'itbi_e3', etiquetas: ['Avaliação em Contraditório'],
      valores: {
        itbi_c1: 'Terreno 14 - Loteamento Morada do Sol',
        ...dadosContribuinte('itbi', { doc: '***.660.371-**', nome: 'Patrícia Exemplo Nunes', whats: '(00) 99999-0202', insc: '04.01.006.0014.000', canal: 'Balcão', prot: '2026/ITBI-001190', bairro: 'Morada do Sol' }),
        itbi_c2: 'Loteadora Exemplo S.A.', itbi_c3: 'Compra e venda', itbi_c4: 60000, itbi_c9: 145000, itbi_c10: 'Não',
        itbi_c13: 'Declarado 59% abaixo da referência e de três vendas recentes na mesma quadra.', itbi_c14: 138000,
        itbi_c15: 'Comparativo com 5 transações registradas na quadra nos últimos 12 meses, ajustadas por área e frente.', itbi_c17: 'Sim',
      },
    },
    {
      id: 'itbi_card3', titulo: 'Casa - Rua dos Ipês, 55', etapaId: 'itbi_e2', etiquetas: ['Escritura Agendada'],
      valores: {
        itbi_c1: 'Casa - Rua dos Ipês, 55',
        ...dadosContribuinte('itbi', { doc: '***.149.530-**', nome: 'Marcos Exemplo Ribeiro', whats: '(00) 99999-0203', insc: '02.05.017.0055.000', canal: 'WhatsApp', prot: '2026/ITBI-001221', bairro: 'Jardim das Flores' }),
        itbi_c2: 'Espólio de Exemplo', itbi_c3: 'Compra e venda', itbi_c4: 380000, itbi_c7: '2º Tabelionato de Notas',
      },
    },
    {
      id: 'itbi_card4', titulo: 'Galpão - integralização ao capital social', etapaId: 'itbi_e6', etiquetas: ['Imunidade / Não Incidência'],
      valores: {
        itbi_c1: 'Galpão - integralização ao capital social',
        ...dadosContribuinte('itbi', { doc: '11.444.777/0001-61', nome: 'Logística Exemplo Ltda', email: 'contato@logistica-exemplo.example.com', insc: '05.03.002.0010.000', canal: 'Portal', prot: '2026/ITBI-001102', bairro: 'Bairro Industrial' }),
        itbi_c2: 'Sócio Fundador Exemplo', itbi_c3: 'Integralização de capital', itbi_c4: 1200000, itbi_c30: 'CQ-2026-000318', itbi_c32: 'Sim',
      },
    },
    {
      id: 'itbi_card5', titulo: 'Sala 08 - Galeria Central', etapaId: 'itbi_e5',
      valores: {
        itbi_c1: 'Sala 08 - Galeria Central',
        ...dadosContribuinte('itbi', { doc: '***.512.338-**', nome: 'Luana Exemplo Costa', whats: '(00) 99999-0204', insc: '01.01.002.0080.008', canal: 'Portal', prot: '2026/ITBI-001175', bairro: 'Centro' }),
        itbi_c2: 'Investimentos Exemplo Ltda', itbi_c3: 'Compra e venda', itbi_c4: 210000, itbi_c10: 'Sim', itbi_c21: 210000, itbi_c23: 4200, itbi_c28: 4200,
      },
    },
  ],
  tour: [
    '🔑 ITBI do formulário à certidão de quitação, com o rito do STJ embutido.',
    '1️⃣ "Declaração": adquirente, transmitente, natureza e o valor declarado da transação.',
    '2️⃣ "Conferência": valor dentro da faixa de referência? o card vai direto para a guia.',
    '3️⃣ "Avaliação": fora da faixa abre avaliação com laudo, notificação e prazo de contestação — o fisco não arbitra sem contraditório.',
    '4️⃣ "Guia Emitida": base, alíquota e vencimento da guia no card.',
    '✅ "Pago" e "Quitação": baixa conferida emite a certidão e avisa o cartório.',
    '📊 Filtre por natureza da transação para separar SFH, leilão e integralização.',
  ],
  etapasDicas: [
    { quemFaz: 'Cartório / adquirente / Portal', objetivo: 'Declarar com matrícula e contrato anexos.' },
    { quemFaz: 'Fiscal do ITBI', objetivo: 'Comparar com a referência sem impor o valor do cadastro.', automacoes: ['Já configurado: dentro da faixa vai direto para a guia'] },
    { quemFaz: 'Comissão de avaliação', objetivo: 'Fundamentar o arbitramento e garantir prazo de contestação (CTN art. 148).' },
    { quemFaz: 'Setor de ITBI', objetivo: 'Emitir a guia e acompanhar o vencimento.' },
    { quemFaz: 'Arrecadação', objetivo: 'Conferir a baixa bancária antes de liberar a quitação.' },
    { quemFaz: 'Setor de ITBI / Cadastro', objetivo: 'Emitir a quitação, avisar o cartório e transferir a titularidade.', conexoes: ['Ligue ao "IPTU: Revisão" quando o adquirente contestar o valor venal'] },
  ],
};

// ============================================================
// A3 — ISS: cadastro mobiliário
// ============================================================
const issCadastro: DefaultTemplate = {
  id: 'tpl_iss_cadastro_mobiliario',
  name: 'ISS: Cadastro Mobiliário',
  descricao: 'Inscrição, alteração e baixa no cadastro mobiliário. Atividade de baixo risco é deferida direto, sem ato público de liberação (Lei 13.874/2019); as demais passam por análise fiscal, com enquadramento do ISS e exigências controladas.',
  categoria: 'arrecadacao',
  icone: '🏪',
  etiquetas: [
    { nome: 'Baixo Risco - Deferido Direto', cor: COR.verde },
    { nome: 'Fiscalização Posterior', cor: COR.amarelo },
    { nome: 'Débito em Aberto', cor: COR.vermelho },
  ],
  etapas: [
    {
      id: 'iss_e1', nome: 'Requerimento',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['iss_c1', 'iss_c2', 'iss_c3'],
      permiteMoverPara: ['iss_e2'],
      campos: [
        { id: 'iss_c1', nome: 'Empresa / Atividade', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('iss', 'mobiliaria'),
        { id: 'iss_c2', nome: 'Tipo de Pedido', tipo: 'lista', obrigatorio: true, opcoes: ['Inscrição', 'Alteração cadastral', 'Baixa'], coresOpcoes: { 'Inscrição': COR.verde, 'Alteração cadastral': COR.azul, 'Baixa': COR.cinza } },
        { id: 'iss_c3', nome: 'CNAE Principal', tipo: 'texto', obrigatorio: true },
        { id: 'iss_c4', nome: 'CNAEs Secundários', tipo: 'texto', obrigatorio: false },
        { id: 'iss_c5', nome: 'Endereço do Estabelecimento', tipo: 'texto', obrigatorio: false },
        { id: 'iss_c6', nome: 'Porte / Natureza', tipo: 'lista', obrigatorio: false, opcoes: ['MEI', 'ME / EPP (Simples Nacional)', 'Demais empresas', 'Autônomo / profissional liberal', 'Sociedade de profissionais'], coresOpcoes: { 'MEI': COR.ciano } },
        { id: 'iss_c7', nome: 'Documentos (contrato social, cartão CNPJ, viabilidade)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'iss_e2', nome: 'Classificação de Risco',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['iss_c1', 'iss_c3', 'iss_c8'],
      permiteMoverPara: ['iss_e3', 'iss_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'iss_c8', nome: 'Grau de Risco da Atividade', tipo: 'lista', obrigatorio: true, opcoes: ['Baixo risco', 'Médio risco', 'Alto risco'], coresOpcoes: { 'Baixo risco': COR.verde, 'Médio risco': COR.amarelo, 'Alto risco': COR.vermelho } },
        { id: 'iss_c9', nome: 'Base legal', tipo: 'texto_informativo', obrigatorio: false, textoInformativo: 'Lei 13.874/2019 (Liberdade Econômica), art. 3º, I: atividade de baixo risco dispensa ato público de liberação. A inscrição é deferida direto e a fiscalização vem depois.' },
        { id: 'iss_c10', nome: 'Fonte da Classificação', tipo: 'lista', obrigatorio: false, opcoes: ['Resolução CGSIM', 'Lei / decreto municipal', 'REDESIM'] },
      ],
    },
    {
      id: 'iss_e3', nome: 'Análise Fiscal',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['iss_c1', 'iss_c12', 'iss_c16'],
      permiteMoverPara: ['iss_e4', 'iss_e5', 'iss_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['iss_c16'],
      campos: [
        { id: 'iss_c11', nome: 'Débitos em aberto?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: { 'Sim': COR.vermelho, 'Não': COR.verde } },
        { id: 'iss_c12', nome: 'Enquadramento do ISS', tipo: 'lista', obrigatorio: false, opcoes: ['ISS próprio (alíquota normal)', 'ISS fixo (autônomo / sociedade)', 'Simples Nacional', 'MEI (SIMEI)', 'Não contribuinte do ISS'] },
        { id: 'iss_c13', nome: 'Alíquota (%)', tipo: 'numero', obrigatorio: false, valorMinimo: 2, valorMaximo: 5 },
        { id: 'iss_c14', nome: 'Precisa de vistoria?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'iss_c15', nome: 'Parecer do Fiscal', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iss_c16', nome: 'Resultado da Análise', tipo: 'lista', obrigatorio: true, opcoes: ['Deferir', 'Exigir documento', 'Indeferir'], coresOpcoes: { 'Deferir': COR.verde, 'Exigir documento': COR.amarelo, 'Indeferir': COR.vermelho } },
      ],
    },
    {
      id: 'iss_e4', nome: 'Pendência do Contribuinte',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['iss_c1', 'iss_whats', 'iss_c18'],
      permiteMoverPara: ['iss_e3', 'iss_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['iss_c20'],
      campos: [
        { id: 'iss_c17', nome: 'Exigência', tipo: 'texto_longo', obrigatorio: true },
        { id: 'iss_c18', nome: 'Prazo para Atender', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'iss_c19', nome: 'Documento Entregue', tipo: 'anexo', obrigatorio: false },
        { id: 'iss_c20', nome: 'Exigência atendida?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iss_e5', nome: 'Deferido',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['iss_c1', 'iss_insc', 'iss_c2'],
      permiteCriarCards: false,
      campos: [
        { id: 'iss_c21', nome: 'Nº da Inscrição Mobiliária', tipo: 'texto', obrigatorio: false },
        { id: 'iss_c22', nome: 'Data do Deferimento', tipo: 'data', obrigatorio: false },
        { id: 'iss_c23', nome: 'Comprovante de Inscrição / Baixa', tipo: 'anexo', obrigatorio: false },
        { id: 'iss_c24', nome: 'Contribuinte avisado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'iss_e6', nome: 'Indeferido',
      camposVisiveisNoCard: ['iss_c1', 'iss_c25', 'iss_c27'],
      permiteMoverPara: ['iss_e3'],
      permiteCriarCards: false,
      campos: [
        { id: 'iss_c25', nome: 'Motivo', tipo: 'lista', obrigatorio: true, opcoes: ['Atividade não permitida no endereço', 'Documentação não apresentada', 'Duplicidade de inscrição', 'Dados divergentes da Receita Federal', 'Outro'] },
        { id: 'iss_c26', nome: 'Fundamentação', tipo: 'texto_longo', obrigatorio: false },
        { id: 'iss_c27', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Baixo risco é deferido direto (Lei 13.874); demais vão à análise fiscal',
      etapaOrigemId: 'iss_e2',
      condicao: { campoId: 'iss_c8', operador: 'igual', valor: 'Baixo risco' },
      etapaDestinoId: 'iss_e5',
      etapaDestinoSenaoId: 'iss_e3',
    },
    {
      tipo: 'mover',
      nome: 'Análise deferida move para Deferido',
      etapaOrigemId: 'iss_e3',
      condicao: { campoId: 'iss_c16', operador: 'igual', valor: 'Deferir' },
      etapaDestinoId: 'iss_e5',
    },
    {
      tipo: 'mover',
      nome: 'Exigência de documento abre pendência',
      etapaOrigemId: 'iss_e3',
      condicao: { campoId: 'iss_c16', operador: 'igual', valor: 'Exigir documento' },
      etapaDestinoId: 'iss_e4',
    },
    {
      tipo: 'mover',
      nome: 'Análise indeferida move para Indeferido',
      etapaOrigemId: 'iss_e3',
      condicao: { campoId: 'iss_c16', operador: 'igual', valor: 'Indeferir' },
      etapaDestinoId: 'iss_e6',
    },
    {
      tipo: 'mover',
      nome: 'Exigência atendida volta para análise',
      etapaOrigemId: 'iss_e4',
      condicao: { campoId: 'iss_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'iss_e3',
    },
  ],
  cards: [
    {
      id: 'iss_card1', titulo: 'Inscrição - Salão de Beleza Exemplo', etapaId: 'iss_e5', etiquetas: ['Baixo Risco - Deferido Direto', 'Fiscalização Posterior'],
      valores: {
        iss_c1: 'Inscrição - Salão de Beleza Exemplo',
        ...dadosContribuinte('iss', { doc: '22.333.444/0001-05', nome: 'Salão de Beleza Exemplo ME', whats: '(00) 99999-0301', insc: '2026.00871', canal: 'Portal', prot: '2026/ISS-000655', bairro: 'Vila Nova' }),
        iss_c2: 'Inscrição', iss_c3: '9602-5/01 Cabeleireiros, manicure e pedicure', iss_c6: 'ME / EPP (Simples Nacional)', iss_c8: 'Baixo risco', iss_c21: '2026.00871', iss_c24: 'Sim',
      },
    },
    {
      id: 'iss_card2', titulo: 'Inscrição - Clínica Odontológica Exemplo', etapaId: 'iss_e3',
      valores: {
        iss_c1: 'Inscrição - Clínica Odontológica Exemplo',
        ...dadosContribuinte('iss', { doc: '33.444.555/0001-10', nome: 'Clínica Odontológica Exemplo Ltda', email: 'adm@clinica-exemplo.example.com', canal: 'Portal', prot: '2026/ISS-000661', bairro: 'Centro' }),
        iss_c2: 'Inscrição', iss_c3: '8630-5/04 Atividade odontológica', iss_c6: 'Sociedade de profissionais', iss_c8: 'Médio risco', iss_c12: 'ISS fixo (autônomo / sociedade)', iss_c14: 'Sim',
      },
    },
    {
      id: 'iss_card3', titulo: 'Alteração de endereço - Oficina Exemplo', etapaId: 'iss_e4', etiquetas: ['Débito em Aberto'],
      valores: {
        iss_c1: 'Alteração de endereço - Oficina Exemplo',
        ...dadosContribuinte('iss', { doc: '44.555.666/0001-20', nome: 'Oficina Mecânica Exemplo Ltda', whats: '(00) 99999-0302', insc: '2019.00342', canal: 'Balcão', prot: '2026/ISS-000640', bairro: 'Bairro Industrial' }),
        iss_c2: 'Alteração cadastral', iss_c3: '4520-0/01 Manutenção mecânica de veículos', iss_c8: 'Médio risco', iss_c11: 'Sim', iss_c16: 'Exigir documento',
        iss_c17: 'Apresentar contrato de locação do novo endereço e parcelar ou quitar o ISS de 2025 em aberto.',
      },
    },
    {
      id: 'iss_card4', titulo: 'Baixa - Consultoria Exemplo', etapaId: 'iss_e2',
      valores: {
        iss_c1: 'Baixa - Consultoria Exemplo',
        ...dadosContribuinte('iss', { doc: '55.666.777/0001-30', nome: 'Consultoria Exemplo EIRELI', email: 'socio@consultoria-exemplo.example.com', insc: '2015.00118', canal: 'Portal', prot: '2026/ISS-000668', bairro: 'Centro' }),
        iss_c2: 'Baixa', iss_c3: '7020-4/00 Consultoria em gestão empresarial',
      },
    },
    {
      id: 'iss_card5', titulo: 'Inscrição - Lanchonete Exemplo', etapaId: 'iss_e1',
      valores: {
        iss_c1: 'Inscrição - Lanchonete Exemplo',
        ...dadosContribuinte('iss', { doc: '66.777.888/0001-40', nome: 'Lanchonete Exemplo MEI', whats: '(00) 99999-0303', canal: 'WhatsApp', prot: '2026/ISS-000671', bairro: 'Morada do Sol' }),
        iss_c2: 'Inscrição', iss_c3: '5611-2/03 Lanchonetes e similares', iss_c6: 'MEI',
      },
    },
  ],
  tour: [
    '🏪 Inscrição, alteração e baixa no cadastro mobiliário num quadro só.',
    '1️⃣ "Requerimento": CNAE, porte e documentos da empresa.',
    '2️⃣ "Classificação de Risco": baixo risco é deferido direto, sem esperar fiscal — é a Lei de Liberdade Econômica.',
    '3️⃣ "Análise Fiscal": enquadramento do ISS, alíquota e débitos em aberto; o resultado já move o card.',
    '4️⃣ "Pendência": exigência com prazo; atendida, volta para a análise.',
    '✅ "Deferido": número da inscrição e aviso ao contribuinte pelo WhatsApp.',
    '📌 Marque "Fiscalização Posterior" nos deferidos direto para a equipe de campo programar a visita.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Portal / REDESIM', objetivo: 'Receber o pedido com CNAE e documentos da empresa.' },
    { quemFaz: 'Cadastro mobiliário', objetivo: 'Classificar o risco pela tabela oficial.', automacoes: ['Já configurado: baixo risco vai direto para Deferido'] },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Enquadrar o ISS e decidir: deferir, exigir ou indeferir.' },
    { quemFaz: 'Atendimento', objetivo: 'Cobrar a exigência e controlar o prazo do contribuinte.' },
    { quemFaz: 'Cadastro mobiliário', objetivo: 'Gerar a inscrição e avisar o contribuinte.', conexoes: ['Ligue ao "Alvará de Funcionamento Integrado"'] },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Fundamentar o indeferimento e informar o prazo de recurso.' },
  ],
};

// ============================================================
// A4 — NFS-e, MEI e Simples Nacional
// ============================================================
const nfseSimples: DefaultTemplate = {
  id: 'tpl_nfse_mei_simples',
  name: 'NFS-e, MEI e Simples Nacional',
  descricao: 'Credenciamento para emitir NFS-e, cancelamento ou substituição de nota fora do prazo e pendência municipal que impede a opção pelo Simples. Cada assunto tem a sua coluna e a janela de janeiro do Simples fica sob controle.',
  categoria: 'arrecadacao',
  icone: '🧾',
  etiquetas: [
    { nome: 'Janela do Simples (janeiro)', cor: COR.vermelho },
    { nome: 'MEI', cor: COR.ciano },
    { nome: 'ISS já recolhido', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'nfse_e1', nome: 'Solicitação',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c2', 'nfse_c3'],
      permiteMoverPara: ['nfse_e2'],
      campos: [
        { id: 'nfse_c1', nome: 'Assunto', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('nfse', 'mobiliaria', { inscricaoObrigatoria: true }),
        { id: 'nfse_c2', nome: 'Tipo de Solicitação', tipo: 'lista', obrigatorio: true, opcoes: ['Credenciamento para emitir NFS-e', 'Cancelamento ou substituição de NFS-e fora do prazo', 'Pendência na opção pelo Simples Nacional', 'Dúvida / desenquadramento MEI'], coresOpcoes: { 'Pendência na opção pelo Simples Nacional': COR.vermelho, 'Dúvida / desenquadramento MEI': COR.ciano } },
        { id: 'nfse_c3', nome: 'Regime Tributário', tipo: 'lista', obrigatorio: false, opcoes: ['MEI', 'Simples Nacional', 'Lucro Presumido / Real'], coresOpcoes: { 'MEI': COR.ciano, 'Simples Nacional': COR.azul } },
        { id: 'nfse_c4', nome: 'Nº da NFS-e', tipo: 'texto', obrigatorio: false },
        { id: 'nfse_c5', nome: 'Valor da Nota', tipo: 'valor', obrigatorio: false },
        { id: 'nfse_c6', nome: 'Motivo Informado', tipo: 'texto_longo', obrigatorio: false },
        { id: 'nfse_c7', nome: 'Anexos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'nfse_e2', nome: 'Triagem',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c8', 'nfse_c9'],
      permiteMoverPara: ['nfse_e3', 'nfse_e4', 'nfse_e5', 'nfse_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['nfse_c8'],
      campos: [
        { id: 'nfse_c8', nome: 'Fila de Atendimento', tipo: 'lista', obrigatorio: true, opcoes: ['Credenciamento', 'Cancelamento de nota', 'Simples / MEI', 'Resposta direta'], coresOpcoes: { 'Simples / MEI': COR.vermelho } },
        { id: 'nfse_c9', nome: 'Cadastro mobiliário ativo?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'nfse_e3', nome: 'Credenciamento NFS-e',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['nfse_c1', 'nfse_insc', 'nfse_c12'],
      permiteMoverPara: ['nfse_e6', 'nfse_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'nfse_c10', nome: 'Usuário criado no emissor', tipo: 'texto', obrigatorio: false },
        { id: 'nfse_c11', nome: 'Série / RPS autorizado', tipo: 'texto', obrigatorio: false },
        { id: 'nfse_c12', nome: 'Credenciado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'nfse_e4', nome: 'Cancelamento Fora do Prazo',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c4', 'nfse_c16'],
      permiteMoverPara: ['nfse_e6', 'nfse_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'nfse_c13', nome: 'Tomador concorda com o cancelamento?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'nfse_c14', nome: 'Declaração do Tomador', tipo: 'anexo', obrigatorio: false },
        { id: 'nfse_c15', nome: 'ISS da nota já recolhido?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'nfse_c16', nome: 'Parecer', tipo: 'lista', obrigatorio: true, opcoes: ['Deferido - cancelar nota', 'Deferido - substituir nota', 'Indeferido'], coresOpcoes: { 'Deferido - cancelar nota': COR.verde, 'Deferido - substituir nota': COR.verde, 'Indeferido': COR.vermelho } },
        { id: 'nfse_c17', nome: 'Fundamentação', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'nfse_e5', nome: 'Pendência na Janela do Simples',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c18', 'nfse_c19'],
      permiteMoverPara: ['nfse_e6', 'nfse_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'nfse_c18', nome: 'Pendências Encontradas', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Débito de ISS', 'Débito de taxas', 'Inscrição municipal irregular', 'Endereço divergente do CNPJ', 'Falta de alvará'] },
        { id: 'nfse_c19', nome: 'Prazo Final da Opção (último dia útil de janeiro)', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'nfse_c20', nome: 'Parcelamento / Comprovante de Regularização', tipo: 'anexo', obrigatorio: false },
        { id: 'nfse_c21', nome: 'Pendência regularizada?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'nfse_e6', nome: 'Concluído',
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c2', 'nfse_c23'],
      permiteCriarCards: false,
      campos: [
        { id: 'nfse_c22', nome: 'Resposta ao Contribuinte', tipo: 'texto_longo', obrigatorio: false },
        { id: 'nfse_c23', nome: 'Data da Conclusão', tipo: 'data', obrigatorio: false },
        { id: 'nfse_c24', nome: 'Comprovante', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'nfse_e7', nome: 'Indeferido',
      camposVisiveisNoCard: ['nfse_c1', 'nfse_c25', 'nfse_c26'],
      permiteMoverPara: ['nfse_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'nfse_c25', nome: 'Motivo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'nfse_c26', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Número e valor da nota só para cancelamento',
      etapaOrigemId: 'nfse_e1',
      condicao: { campoId: 'nfse_c2', operador: 'igual', valor: 'Cancelamento ou substituição de NFS-e fora do prazo' },
      camposAfetadosIds: ['nfse_c4', 'nfse_c5'],
    },
    {
      tipo: 'mover',
      nome: 'Fila Credenciamento',
      etapaOrigemId: 'nfse_e2',
      condicao: { campoId: 'nfse_c8', operador: 'igual', valor: 'Credenciamento' },
      etapaDestinoId: 'nfse_e3',
    },
    {
      tipo: 'mover',
      nome: 'Fila Cancelamento de nota',
      etapaOrigemId: 'nfse_e2',
      condicao: { campoId: 'nfse_c8', operador: 'igual', valor: 'Cancelamento de nota' },
      etapaDestinoId: 'nfse_e4',
    },
    {
      tipo: 'mover',
      nome: 'Fila Simples / MEI',
      etapaOrigemId: 'nfse_e2',
      condicao: { campoId: 'nfse_c8', operador: 'igual', valor: 'Simples / MEI' },
      etapaDestinoId: 'nfse_e5',
    },
    {
      tipo: 'mover',
      nome: 'Credenciado conclui o pedido',
      etapaOrigemId: 'nfse_e3',
      condicao: { campoId: 'nfse_c12', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'nfse_e6',
    },
    {
      tipo: 'mover',
      nome: 'Parecer do cancelamento decide o destino',
      etapaOrigemId: 'nfse_e4',
      condicao: { campoId: 'nfse_c16', operador: 'igual', valor: 'Indeferido' },
      etapaDestinoId: 'nfse_e7',
      etapaDestinoSenaoId: 'nfse_e6',
    },
    {
      tipo: 'mover',
      nome: 'Pendência regularizada libera a opção pelo Simples',
      etapaOrigemId: 'nfse_e5',
      condicao: { campoId: 'nfse_c21', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'nfse_e6',
    },
  ],
  cards: [
    {
      id: 'nfse_card1', titulo: 'Débito de ISS impede opção pelo Simples', etapaId: 'nfse_e5', etiquetas: ['Janela do Simples (janeiro)'],
      valores: {
        nfse_c1: 'Débito de ISS impede opção pelo Simples',
        ...dadosContribuinte('nfse', { doc: '77.888.999/0001-50', nome: 'Estúdio Exemplo de Design Ltda', whats: '(00) 99999-0401', insc: '2025.01440', canal: 'WhatsApp', prot: '2026/NFSE-000092', bairro: 'Centro' }),
        nfse_c2: 'Pendência na opção pelo Simples Nacional', nfse_c3: 'Lucro Presumido / Real', nfse_c8: 'Simples / MEI', nfse_c18: ['Débito de ISS'],
      },
    },
    {
      id: 'nfse_card2', titulo: 'Cancelar NFS-e 1.284 emitida com tomador errado', etapaId: 'nfse_e4', etiquetas: ['ISS já recolhido'],
      valores: {
        nfse_c1: 'Cancelar NFS-e 1.284 emitida com tomador errado',
        ...dadosContribuinte('nfse', { doc: '88.999.000/0001-60', nome: 'Manutenção Predial Exemplo Ltda', email: 'fiscal@predial-exemplo.example.com', insc: '2018.00977', canal: 'Portal', prot: '2026/NFSE-000088', bairro: 'Vila Nova' }),
        nfse_c2: 'Cancelamento ou substituição de NFS-e fora do prazo', nfse_c3: 'Simples Nacional', nfse_c4: '1284', nfse_c5: 12600, nfse_c8: 'Cancelamento de nota', nfse_c13: 'Sim', nfse_c15: 'Sim',
      },
    },
    {
      id: 'nfse_card3', titulo: 'Credenciamento - eletricista MEI', etapaId: 'nfse_e3', etiquetas: ['MEI'],
      valores: {
        nfse_c1: 'Credenciamento - eletricista MEI',
        ...dadosContribuinte('nfse', { doc: '99.000.111/0001-70', nome: 'Eletricista Exemplo MEI', whats: '(00) 99999-0402', insc: '2026.00915', canal: 'Balcão', prot: '2026/NFSE-000095', bairro: 'Morada do Sol' }),
        nfse_c2: 'Credenciamento para emitir NFS-e', nfse_c3: 'MEI', nfse_c8: 'Credenciamento', nfse_c9: 'Sim',
      },
    },
    {
      id: 'nfse_card4', titulo: 'MEI ultrapassou o limite de faturamento', etapaId: 'nfse_e2', etiquetas: ['MEI'],
      valores: {
        nfse_c1: 'MEI ultrapassou o limite de faturamento',
        ...dadosContribuinte('nfse', { doc: '10.111.222/0001-80', nome: 'Doces Exemplo MEI', whats: '(00) 99999-0403', insc: '2021.00630', canal: 'Telefone', prot: '2026/NFSE-000097', bairro: 'Jardim das Flores' }),
        nfse_c2: 'Dúvida / desenquadramento MEI', nfse_c3: 'MEI',
      },
    },
  ],
  tour: [
    '🧾 Os pedidos de NFS-e, MEI e Simples separados por assunto, cada um com a sua coluna.',
    '1️⃣ "Solicitação": o tipo de pedido mostra só os campos que importam — número e valor da nota aparecem só no cancelamento.',
    '2️⃣ "Triagem": escolhida a fila, o card vai sozinho para Credenciamento, Cancelamento ou Simples.',
    '3️⃣ "Cancelamento Fora do Prazo": concordância do tomador e ISS já recolhido antes do parecer.',
    '⏰ "Janela do Simples": em janeiro, débito municipal impede a opção — o prazo final fica no card.',
    '✅ "Concluído" e "Indeferido": resposta ao contribuinte registrada, com prazo de recurso quando negado.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Portal', objetivo: 'Registrar com inscrição mobiliária e tipo de pedido.' },
    { quemFaz: 'Atendimento da Fazenda', objetivo: 'Escolher a fila; pedidos simples podem ser respondidos direto.', automacoes: ['Já configurado: cada fila move o card para a sua coluna'] },
    { quemFaz: 'Setor de NFS-e', objetivo: 'Liberar o acesso ao emissor e confirmar o credenciamento.' },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Conferir tomador e ISS recolhido antes de cancelar nota antiga.' },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Destravar a pendência antes do último dia útil de janeiro.', conexoes: ['Ligue ao "Parcelamento e REFIS" para regularizar débitos'] },
    { quemFaz: 'Atendimento', objetivo: 'Responder o contribuinte e anexar o comprovante.' },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Fundamentar a negativa e informar o prazo de recurso.' },
  ],
};

// ============================================================
// A5 — Parcelamento e REFIS da dívida ativa
// ============================================================
const parcelamentoRefis: DefaultTemplate = {
  id: 'tpl_parcelamento_refis',
  name: 'Parcelamento e REFIS da Dívida Ativa',
  descricao: 'Da simulação ao acordo ativo: termo de confissão, conferência dos requisitos do programa, 1ª parcela que formaliza o acordo, acompanhamento das parcelas e, se romper, protesto ou execução fiscal.',
  categoria: 'arrecadacao',
  icone: '🤝',
  etiquetas: [
    { nome: 'REFIS 2026', cor: COR.azul },
    { nome: 'Dívida Ajuizada', cor: COR.roxo },
    { nome: 'Grande Devedor', cor: COR.vermelho },
    { nome: 'Risco de Rompimento', cor: COR.amarelo },
  ],
  etapas: [
    {
      id: 'parc_e1', nome: 'Simulação',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['parc_c1', 'parc_c4', 'parc_c6'],
      permiteMoverPara: ['parc_e2'],
      limparCamposAoRetornar: ['parc_c10'],
      campos: [
        { id: 'parc_c1', nome: 'Devedor / Dívida', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('parc', 'ambas'),
        { id: 'parc_c2', nome: 'Nº da(s) CDA(s)', tipo: 'texto', obrigatorio: false },
        { id: 'parc_c3', nome: 'Tributos Incluídos', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['IPTU', 'ISS', 'ITBI', 'Taxas', 'Multas', 'Contribuição de melhoria'] },
        { id: 'parc_c4', nome: 'Valor Consolidado da Dívida', tipo: 'valor', obrigatorio: true },
        { id: 'parc_c5', nome: 'Programa', tipo: 'lista', obrigatorio: true, opcoes: ['Parcelamento ordinário', 'REFIS 2026', 'Transação tributária'], coresOpcoes: { 'REFIS 2026': COR.azul } },
        { id: 'parc_c6', nome: 'Nº de Parcelas', tipo: 'numero', obrigatorio: false, valorMinimo: 1, valorMaximo: 120, precisao: 0 },
        { id: 'parc_c7', nome: 'Desconto de Juros e Multa (%)', tipo: 'numero', obrigatorio: false, valorMinimo: 0, valorMaximo: 100, precisao: 0 },
        { id: 'parc_c8', nome: 'Dívida já ajuizada?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: { 'Sim': COR.roxo, 'Não': COR.cinza } },
        { id: 'parc_c9', nome: 'Simulação (PDF)', tipo: 'anexo', obrigatorio: false },
        { id: 'parc_c10', nome: 'Contribuinte aceitou a simulação?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'parc_e2', nome: 'Adesão',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['parc_c1', 'parc_c5', 'parc_c13'],
      permiteMoverPara: ['parc_e3', 'parc_e1'],
      permiteCriarCards: false,
      campos: [
        { id: 'parc_c11', nome: 'Termo de confissão de dívida assinado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'parc_c12', nome: 'Termo de Adesão / Confissão', tipo: 'anexo', obrigatorio: false },
        { id: 'parc_c13', nome: 'Valor da Parcela', tipo: 'valor', obrigatorio: false },
        { id: 'parc_c14', nome: 'Procuração (se representante)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'parc_e3', nome: 'Conferência',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['parc_c1', 'parc_c4', 'parc_c15'],
      permiteMoverPara: ['parc_e4', 'parc_e1'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'parc_c15', nome: 'Requisitos do programa atendidos?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'parc_c16', nome: 'Parcela mínima respeitada?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'parc_c17', nome: 'Custas e honorários incluídos (dívida ajuizada)?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
        { id: 'parc_c18', nome: 'Observação da Conferência', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'parc_e4', nome: '1ª Parcela',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['parc_c1', 'parc_c13', 'parc_c19'],
      permiteMoverPara: ['parc_e5', 'parc_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'parc_c19', nome: 'Vencimento da 1ª Parcela', tipo: 'data_vencimento', obrigatorio: true },
        { id: 'parc_c20', nome: 'Guia da 1ª Parcela', tipo: 'anexo', obrigatorio: false },
        { id: 'parc_c21', nome: '1ª parcela paga?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'parc_e5', nome: 'Acordo Ativo',
      camposVisiveisNoCard: ['parc_c1', 'parc_c22', 'parc_c24'],
      permiteMoverPara: ['parc_e6', 'parc_e8'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'parc_c22', nome: 'Parcelas Pagas', tipo: 'numero', obrigatorio: false, valorMinimo: 0, precisao: 0 },
        { id: 'parc_c23', nome: 'Parcelas em Atraso', tipo: 'numero', obrigatorio: false, valorMinimo: 0, precisao: 0 },
        { id: 'parc_c24', nome: 'Próximo Vencimento', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'parc_c25', nome: 'Suspensão da execução peticionada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
      ],
    },
    {
      id: 'parc_e6', nome: 'Rompido',
      tempoMaximoHoras: 168,
      camposVisiveisNoCard: ['parc_c1', 'parc_c26', 'parc_c27'],
      permiteMoverPara: ['parc_e5', 'parc_e7'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['parc_c29'],
      campos: [
        { id: 'parc_c26', nome: 'Motivo da Rescisão', tipo: 'lista', obrigatorio: false, opcoes: ['Parcelas em atraso acima do limite', '1ª parcela não paga', 'Pedido do contribuinte', 'Descumprimento de requisito do programa'] },
        { id: 'parc_c27', nome: 'Saldo Remanescente', tipo: 'valor', obrigatorio: false },
        { id: 'parc_c28', nome: 'Contribuinte notificado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'parc_c29', nome: 'Encaminhamento', tipo: 'lista', obrigatorio: true, opcoes: ['Retomar acordo', 'Protesto da CDA', 'Execução fiscal'], coresOpcoes: { 'Retomar acordo': COR.verde, 'Protesto da CDA': COR.amarelo, 'Execução fiscal': COR.vermelho } },
      ],
    },
    {
      id: 'parc_e7', nome: 'Protesto / Execução',
      tempoMaximoHoras: 720,
      camposVisiveisNoCard: ['parc_c1', 'parc_c30', 'parc_c31'],
      permiteMoverPara: ['parc_e1', 'parc_e8'],
      permiteCriarCards: false,
      campos: [
        { id: 'parc_c30', nome: 'Medida Adotada', tipo: 'lista', obrigatorio: false, opcoes: ['Protesto da CDA', 'Execução fiscal ajuizada', 'Execução retomada'], coresOpcoes: { 'Execução fiscal ajuizada': COR.vermelho, 'Execução retomada': COR.vermelho } },
        { id: 'parc_c31', nome: 'Nº do Protesto / Processo', tipo: 'texto', obrigatorio: false },
        { id: 'parc_c32', nome: 'Data', tipo: 'data', obrigatorio: false },
        { id: 'parc_c33', nome: 'Documentos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'parc_e8', nome: 'Quitado',
      camposVisiveisNoCard: ['parc_c1', 'parc_c4', 'parc_c34'],
      permiteCriarCards: false,
      campos: [
        { id: 'parc_c34', nome: 'Data da Quitação', tipo: 'data', obrigatorio: false },
        { id: 'parc_c35', nome: 'Extinção da execução peticionada?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
        { id: 'parc_c36', nome: 'Protesto cancelado?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Simulação aceita vai para adesão',
      etapaOrigemId: 'parc_e1',
      condicao: { campoId: 'parc_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'parc_e2',
    },
    {
      tipo: 'mover',
      nome: 'Termo assinado segue para conferência',
      etapaOrigemId: 'parc_e2',
      condicao: { campoId: 'parc_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'parc_e3',
    },
    {
      tipo: 'mover',
      nome: 'Requisitos atendidos emitem a 1ª parcela; senão volta à simulação',
      etapaOrigemId: 'parc_e3',
      condicao: { campoId: 'parc_c15', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'parc_e4',
      etapaDestinoSenaoId: 'parc_e1',
    },
    {
      tipo: 'mover',
      nome: '1ª parcela paga ativa o acordo',
      etapaOrigemId: 'parc_e4',
      condicao: { campoId: 'parc_c21', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'parc_e5',
    },
    {
      tipo: 'mover',
      nome: '3 parcelas em atraso rompem o acordo',
      etapaOrigemId: 'parc_e5',
      condicao: { campoId: 'parc_c23', operador: 'maior', valor: 2 },
      etapaDestinoId: 'parc_e6',
    },
    {
      tipo: 'mover',
      nome: 'Rompido: retomar acordo ou cobrar',
      etapaOrigemId: 'parc_e6',
      condicao: { campoId: 'parc_c29', operador: 'igual', valor: 'Retomar acordo' },
      etapaDestinoId: 'parc_e5',
      etapaDestinoSenaoId: 'parc_e7',
    },
  ],
  cards: [
    {
      id: 'parc_card1', titulo: 'IPTU 2022 a 2025 - Rua das Palmeiras, 900', etapaId: 'parc_e5', etiquetas: ['REFIS 2026'],
      valores: {
        parc_c1: 'IPTU 2022 a 2025 - Rua das Palmeiras, 900',
        ...dadosContribuinte('parc', { doc: '***.884.019-**', nome: 'Sandra Exemplo Alves', whats: '(00) 99999-0501', insc: '03.02.015.0900.000', canal: 'Portal', prot: '2026/REFIS-002310', bairro: 'Vila Nova' }),
        parc_c2: 'CDA 4512/2023; 4513/2024', parc_c3: ['IPTU'], parc_c4: 9840, parc_c5: 'REFIS 2026', parc_c6: 24, parc_c7: 90, parc_c13: 410, parc_c22: 5, parc_c23: 0,
      },
    },
    {
      id: 'parc_card2', titulo: 'ISS 2024 - Transportadora Exemplo', etapaId: 'parc_e6', etiquetas: ['Grande Devedor', 'Dívida Ajuizada'],
      valores: {
        parc_c1: 'ISS 2024 - Transportadora Exemplo',
        ...dadosContribuinte('parc', { doc: '12.121.212/0001-90', nome: 'Transportadora Exemplo Ltda', email: 'financeiro@transp-exemplo.example.com', insc: '2012.00204', canal: 'Balcão', prot: '2026/REFIS-002101', bairro: 'Bairro Industrial' }),
        parc_c3: ['ISS', 'Multas'], parc_c4: 386000, parc_c5: 'Parcelamento ordinário', parc_c6: 60, parc_c8: 'Sim', parc_c23: 4,
        parc_c26: 'Parcelas em atraso acima do limite', parc_c27: 301500, parc_c28: 'Sim',
      },
    },
    {
      id: 'parc_card3', titulo: 'Taxas e IPTU - Mercearia Exemplo', etapaId: 'parc_e4', etiquetas: ['REFIS 2026'],
      valores: {
        parc_c1: 'Taxas e IPTU - Mercearia Exemplo',
        ...dadosContribuinte('parc', { doc: '13.131.313/0001-00', nome: 'Mercearia Exemplo ME', whats: '(00) 99999-0502', insc: '2017.00559', canal: 'WhatsApp', prot: '2026/REFIS-002355', bairro: 'Jardim das Flores' }),
        parc_c3: ['IPTU', 'Taxas'], parc_c4: 14200, parc_c5: 'REFIS 2026', parc_c6: 12, parc_c7: 80, parc_c13: 1050, parc_c15: 'Sim',
      },
    },
    {
      id: 'parc_card4', titulo: 'Multa de posturas - simulação', etapaId: 'parc_e1',
      valores: {
        parc_c1: 'Multa de posturas - simulação',
        ...dadosContribuinte('parc', { doc: '***.274.663-**', nome: 'Diego Exemplo Martins', whats: '(00) 99999-0503', canal: 'Telefone', prot: '2026/REFIS-002371', bairro: 'Centro' }),
        parc_c3: ['Multas'], parc_c4: 3150, parc_c5: 'Parcelamento ordinário', parc_c6: 6,
      },
    },
    {
      id: 'parc_card5', titulo: 'IPTU 2019 a 2021 - execução fiscal', etapaId: 'parc_e7', etiquetas: ['Dívida Ajuizada'],
      valores: {
        parc_c1: 'IPTU 2019 a 2021 - execução fiscal',
        ...dadosContribuinte('parc', { doc: '***.509.882-**', nome: 'Espólio de Exemplo Souza', insc: '04.03.008.0041.000', canal: 'Balcão', prot: '2025/REFIS-001877', bairro: 'Morada do Sol' }),
        parc_c3: ['IPTU'], parc_c4: 22700, parc_c8: 'Sim', parc_c29: 'Execução fiscal', parc_c30: 'Execução retomada', parc_c31: '5000123-45.2025.8.00.0001',
      },
    },
  ],
  tour: [
    '🤝 A negociação da dívida ativa do primeiro contato ao acordo quitado.',
    '1️⃣ "Simulação": CDAs, tributos, programa (ordinário ou REFIS) e desconto aplicado.',
    '2️⃣ "Adesão" e "Conferência": termo de confissão assinado e requisitos do programa conferidos — falhou, volta à simulação.',
    '3️⃣ "1ª Parcela": o acordo só vale depois que a primeira parcela é paga.',
    '4️⃣ "Acordo Ativo": com 3 parcelas em atraso o card cai sozinho em "Rompido".',
    '⚖️ "Rompido": retomar o acordo ou cobrar por protesto ou execução fiscal.',
    '📊 O valor consolidado no card vira o indicador de quanto o REFIS recuperou.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento da Dívida Ativa', objetivo: 'Simular as opções e registrar a escolha do contribuinte.' },
    { quemFaz: 'Atendimento / contribuinte', objetivo: 'Colher o termo de confissão assinado.' },
    { quemFaz: 'Setor de Dívida Ativa', objetivo: 'Conferir parcela mínima, prazo do programa e custas da execução.', automacoes: ['Já configurado: sem requisito volta à simulação'] },
    { quemFaz: 'Arrecadação', objetivo: 'Acompanhar o pagamento da 1ª parcela — sem ela não há acordo.' },
    { quemFaz: 'Setor de Dívida Ativa', objetivo: 'Atualizar as parcelas e pedir a suspensão da execução quando houver.', automacoes: ['Já configurado: 3 parcelas em atraso rompem o acordo'] },
    { quemFaz: 'Setor de Dívida Ativa', objetivo: 'Notificar o contribuinte e decidir o encaminhamento.' },
    { quemFaz: 'Procuradoria', objetivo: 'Protestar a CDA ou ajuizar/retomar a execução.' },
    { quemFaz: 'Procuradoria / Dívida Ativa', objetivo: 'Pedir extinção da execução e cancelar o protesto.', conexoes: ['Ligue à "Certidão Negativa" para emitir a certidão após a quitação'] },
  ],
};

// ============================================================
// A6 — Certidão negativa / positiva com efeito de negativa
// ============================================================
const certidaoNegativa: DefaultTemplate = {
  id: 'tpl_certidao_negativa',
  name: 'Certidão Negativa de Débitos',
  descricao: 'Pedido de certidão negativa ou positiva com efeito de negativa no prazo legal de 10 dias (CTN art. 205). Débito suspenso libera a certidão; débito exigível vira orientação ao contribuinte, com o relógio do processo parado enquanto ele regulariza.',
  categoria: 'arrecadacao',
  icone: '📜',
  etiquetas: [
    { nome: 'Licitação', cor: COR.azul },
    { nome: 'Prazo Vencendo', cor: COR.vermelho },
    { nome: 'Débito Suspenso', cor: COR.amarelo },
  ],
  // Relógio único de 10 dias para o pedido inteiro (CTN art. 205, parágrafo único).
  // Para na pendência do requerente e nas colunas finais.
  prazoProcesso: { horas: 240, ignorarFimDeSemana: false, etapasPausaIds: ['cnd_e3', 'cnd_e4', 'cnd_e5'] },
  etapas: [
    {
      id: 'cnd_e1', nome: 'Pedido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['cnd_c1', 'cnd_c2', 'cnd_c4'],
      permiteMoverPara: ['cnd_e2'],
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'cnd_c1', nome: 'Requerente / Finalidade', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('cnd', 'ambas'),
        { id: 'cnd_c2', nome: 'Tipo de Certidão', tipo: 'lista', obrigatorio: true, opcoes: ['Negativa de débitos', 'Positiva com efeito de negativa', 'Débitos do imóvel', 'Débitos da empresa', 'Valor venal'] },
        { id: 'cnd_c3', nome: 'Finalidade', tipo: 'lista', obrigatorio: false, opcoes: ['Licitação', 'Financiamento', 'Escritura / venda de imóvel', 'Alvará / regularização', 'Outros'], coresOpcoes: { 'Licitação': COR.azul } },
        { id: 'cnd_c4', nome: 'Prazo Legal (10 dias)', tipo: 'data_vencimento', obrigatorio: true },
      ],
    },
    {
      id: 'cnd_e2', nome: 'Verificação de Débitos',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['cnd_c1', 'cnd_c5', 'cnd_c4'],
      permiteMoverPara: ['cnd_e3', 'cnd_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['cnd_c5'],
      campos: [
        { id: 'cnd_c5', nome: 'Situação Fiscal', tipo: 'lista', obrigatorio: true, opcoes: ['Liberada - sem débitos', 'Liberada - débitos suspensos (CTN art. 151)', 'Pendente - débitos exigíveis', 'Pendente - cadastro irregular'], coresOpcoes: { 'Liberada - sem débitos': COR.verde, 'Liberada - débitos suspensos (CTN art. 151)': COR.amarelo, 'Pendente - débitos exigíveis': COR.vermelho, 'Pendente - cadastro irregular': COR.vermelho } },
        { id: 'cnd_c6', nome: 'Causa da Suspensão', tipo: 'lista', obrigatorio: false, opcoes: ['Parcelamento em dia', 'Impugnação ou recurso pendente', 'Depósito do montante integral', 'Liminar / tutela judicial'] },
        { id: 'cnd_c7', nome: 'Débitos Encontrados', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cnd_c8', nome: 'Valor em Aberto', tipo: 'valor', obrigatorio: false },
      ],
    },
    {
      id: 'cnd_e3', nome: 'Pendência do Requerente',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['cnd_c1', 'cnd_c8', 'cnd_whats'],
      permiteMoverPara: ['cnd_e2', 'cnd_e5'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['cnd_c10', 'cnd_c12'],
      campos: [
        { id: 'cnd_c9', nome: 'Orientação Enviada', tipo: 'texto_longo', obrigatorio: true },
        { id: 'cnd_c10', nome: 'Requerente regularizou?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'cnd_c11', nome: 'Comprovante de Regularização', tipo: 'anexo', obrigatorio: false },
        { id: 'cnd_c12', nome: 'Quer a certidão positiva mesmo assim?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cnd_e4', nome: 'Emitida',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['cnd_c1', 'cnd_c13', 'cnd_c15'],
      permiteCriarCards: false,
      campos: [
        { id: 'cnd_c13', nome: 'Certidão Emitida', tipo: 'lista', obrigatorio: false, opcoes: ['Negativa', 'Positiva com efeito de negativa'], coresOpcoes: { 'Negativa': COR.verde, 'Positiva com efeito de negativa': COR.amarelo } },
        { id: 'cnd_c14', nome: 'Código de Autenticidade', tipo: 'texto', obrigatorio: false },
        { id: 'cnd_c15', nome: 'Validade', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'cnd_c16', nome: 'Certidão (PDF)', tipo: 'anexo', obrigatorio: false },
        { id: 'cnd_c17', nome: 'Enviada ao requerente?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'cnd_e5', nome: 'Positiva (Débito Exigível)',
      camposVisiveisNoCard: ['cnd_c1', 'cnd_c8', 'cnd_c20'],
      permiteMoverPara: ['cnd_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'cnd_c18', nome: 'Débitos que Impedem a Negativa', tipo: 'texto_longo', obrigatorio: false },
        { id: 'cnd_c19', nome: 'Certidão Positiva (PDF)', tipo: 'anexo', obrigatorio: false },
        { id: 'cnd_c20', nome: 'Orientado a parcelar?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Situação liberada emite; pendente orienta o requerente',
      etapaOrigemId: 'cnd_e2',
      condicao: { campoId: 'cnd_c5', operador: 'contem', valor: 'Liberada' },
      etapaDestinoId: 'cnd_e4',
      etapaDestinoSenaoId: 'cnd_e3',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Causa da suspensão só para débito suspenso',
      etapaOrigemId: 'cnd_e2',
      condicao: { campoId: 'cnd_c5', operador: 'igual', valor: 'Liberada - débitos suspensos (CTN art. 151)' },
      camposAfetadosIds: ['cnd_c6'],
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Detalhe dos débitos só quando há pendência',
      etapaOrigemId: 'cnd_e2',
      condicao: { campoId: 'cnd_c5', operador: 'contem', valor: 'Pendente' },
      camposAfetadosIds: ['cnd_c7', 'cnd_c8'],
    },
    {
      tipo: 'mover',
      nome: 'Regularizou volta para nova verificação',
      etapaOrigemId: 'cnd_e3',
      condicao: { campoId: 'cnd_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cnd_e2',
    },
    {
      tipo: 'mover',
      nome: 'Pediu a positiva mesmo assim',
      etapaOrigemId: 'cnd_e3',
      condicao: { campoId: 'cnd_c12', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'cnd_e5',
    },
  ],
  cards: [
    {
      id: 'cnd_card1', titulo: 'CND para licitação - Construtora Exemplo', etapaId: 'cnd_e2', etiquetas: ['Licitação', 'Prazo Vencendo'],
      valores: {
        cnd_c1: 'CND para licitação - Construtora Exemplo',
        ...dadosContribuinte('cnd', { doc: '14.141.414/0001-10', nome: 'Construtora Exemplo Ltda', email: 'licitacao@construtora-exemplo.example.com', insc: '2010.00087', canal: 'Portal', prot: '2026/CND-004410', bairro: 'Centro' }),
        cnd_c2: 'Negativa de débitos', cnd_c3: 'Licitação',
      },
    },
    {
      id: 'cnd_card2', titulo: 'Certidão do imóvel para escritura', etapaId: 'cnd_e4',
      valores: {
        cnd_c1: 'Certidão do imóvel para escritura',
        ...dadosContribuinte('cnd', { doc: '***.637.214-**', nome: 'Vanessa Exemplo Prado', whats: '(00) 99999-0601', insc: '02.01.030.0012.000', canal: 'WhatsApp', prot: '2026/CND-004398', bairro: 'Jardim das Flores' }),
        cnd_c2: 'Débitos do imóvel', cnd_c3: 'Escritura / venda de imóvel', cnd_c5: 'Liberada - sem débitos', cnd_c13: 'Negativa', cnd_c14: 'A1B2-C3D4-E5F6', cnd_c17: 'Sim',
      },
    },
    {
      id: 'cnd_card3', titulo: 'Positiva com efeito de negativa - Padaria Exemplo', etapaId: 'cnd_e4', etiquetas: ['Débito Suspenso'],
      valores: {
        cnd_c1: 'Positiva com efeito de negativa - Padaria Exemplo',
        ...dadosContribuinte('cnd', { doc: '15.151.515/0001-20', nome: 'Padaria Exemplo Ltda', whats: '(00) 99999-0602', insc: '2016.00412', canal: 'Balcão', prot: '2026/CND-004402', bairro: 'Vila Nova' }),
        cnd_c2: 'Positiva com efeito de negativa', cnd_c3: 'Financiamento', cnd_c5: 'Liberada - débitos suspensos (CTN art. 151)', cnd_c6: 'Parcelamento em dia', cnd_c13: 'Positiva com efeito de negativa',
      },
    },
    {
      id: 'cnd_card4', titulo: 'CND pessoa física - ISS de autônomo em aberto', etapaId: 'cnd_e3',
      valores: {
        cnd_c1: 'CND pessoa física - ISS de autônomo em aberto',
        ...dadosContribuinte('cnd', { doc: '***.158.946-**', nome: 'Fernando Exemplo Reis', whats: '(00) 99999-0603', canal: 'Portal', prot: '2026/CND-004415', bairro: 'Morada do Sol' }),
        cnd_c2: 'Negativa de débitos', cnd_c3: 'Outros', cnd_c5: 'Pendente - débitos exigíveis', cnd_c7: 'ISS fixo de autônomo 2025 (2 parcelas).', cnd_c8: 486,
        cnd_c9: 'Débito de R$ 486,00 de ISS 2025. Pode pagar a guia enviada no WhatsApp ou parcelar pelo portal; depois responda aqui para nova verificação.',
      },
    },
  ],
  tour: [
    '📜 Certidão negativa no prazo de 10 dias do CTN, com relógio único para o pedido inteiro.',
    '1️⃣ "Pedido": tipo, finalidade e o prazo legal como vencimento do card.',
    '2️⃣ "Verificação": sem débito ou com débito suspenso (parcelamento, recurso, liminar) a certidão é liberada sozinha.',
    '3️⃣ "Pendência do Requerente": débito exigível vira orientação — o relógio do processo para enquanto ele regulariza.',
    '✅ "Emitida": código de autenticidade, validade e envio ao requerente.',
    '📌 Etiqueta "Licitação" para furar a fila quando a sessão está marcada.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Portal', objetivo: 'Registrar com o prazo legal de 10 dias já preenchido.' },
    { quemFaz: 'Setor de Arrecadação', objetivo: 'Verificar débitos e a causa de suspensão (CTN art. 151).', automacoes: ['Já configurado: situação liberada emite; pendente orienta o requerente'] },
    { quemFaz: 'Atendimento', objetivo: 'Mandar a guia ou o caminho do parcelamento pelo WhatsApp.', conexoes: ['Ligue ao "Parcelamento e REFIS"'] },
    { quemFaz: 'Setor de Arrecadação', objetivo: 'Emitir com código de autenticidade e enviar ao requerente.' },
    { quemFaz: 'Setor de Arrecadação', objetivo: 'Emitir a positiva e orientar o parcelamento.' },
  ],
};

// ============================================================
// A7 — Restituição e compensação
// ============================================================
const restituicao: DefaultTemplate = {
  id: 'tpl_restituicao_compensacao',
  name: 'Restituição e Compensação de Tributos',
  descricao: 'Pagamento em duplicidade, a maior ou de lançamento cancelado: checagem do prazo de 5 anos (CTN art. 168), apuração com correção, parecer da Procuradoria acima da alçada, decisão e crédito em conta ou compensação.',
  categoria: 'arrecadacao',
  icone: '💸',
  etiquetas: [
    { nome: 'Acima da Alçada', cor: COR.roxo },
    { nome: 'Decisão Judicial', cor: COR.vermelho },
    { nome: 'Compensação', cor: COR.ciano },
  ],
  etapas: [
    {
      id: 'rest_e1', nome: 'Pedido',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['rest_c1', 'rest_c2', 'rest_c6'],
      permiteMoverPara: ['rest_e2'],
      campos: [
        { id: 'rest_c1', nome: 'Pedido', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('rest', 'ambas'),
        { id: 'rest_c2', nome: 'Tipo', tipo: 'lista', obrigatorio: true, opcoes: ['Restituição', 'Compensação com débito'], coresOpcoes: { 'Compensação com débito': COR.ciano } },
        { id: 'rest_c3', nome: 'Tributo', tipo: 'lista', obrigatorio: true, opcoes: ['IPTU', 'ISS', 'ITBI', 'Taxas', 'Multa'] },
        { id: 'rest_c4', nome: 'Motivo do Indébito', tipo: 'lista', obrigatorio: true, opcoes: ['Pagamento em duplicidade', 'Pagamento a maior', 'Lançamento revisto ou cancelado', 'ISS retido indevidamente', 'Decisão judicial'], coresOpcoes: { 'Decisão judicial': COR.vermelho } },
        { id: 'rest_c5', nome: 'Data do Pagamento Indevido', tipo: 'data', obrigatorio: true },
        { id: 'rest_c6', nome: 'Valor Pedido', tipo: 'valor', obrigatorio: true },
        { id: 'rest_c7', nome: 'Comprovantes de Pagamento', tipo: 'anexo', obrigatorio: false },
        { id: 'rest_c8', nome: 'Dados Bancários do Titular', tipo: 'texto', obrigatorio: false },
      ],
    },
    {
      id: 'rest_e2', nome: 'Prazo e Admissibilidade',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['rest_c1', 'rest_c5', 'rest_c10'],
      permiteMoverPara: ['rest_e3', 'rest_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'rest_c9', nome: 'Prazo de 5 anos', tipo: 'texto_informativo', obrigatorio: false, textoInformativo: 'CTN art. 168: o direito à restituição se extingue em 5 anos, contados da data do pagamento indevido (inciso I) ou da decisão que reformou ou anulou o lançamento (inciso II).' },
        { id: 'rest_c10', nome: 'Pedido dentro dos 5 anos?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'rest_c11', nome: 'Comprovou que assumiu o encargo (ISS, CTN art. 166)?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
      ],
    },
    {
      id: 'rest_e3', nome: 'Apuração Fiscal',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['rest_c1', 'rest_c12', 'rest_c13'],
      permiteMoverPara: ['rest_e4', 'rest_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'rest_c12', nome: 'Valor Apurado (com correção)', tipo: 'valor', obrigatorio: true },
        { id: 'rest_c13', nome: 'Pagamento localizado no sistema?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'rest_c14', nome: 'Débitos em Aberto para Compensar', tipo: 'valor', obrigatorio: false },
        { id: 'rest_c15', nome: 'Memória de Cálculo', tipo: 'anexo', obrigatorio: false },
        // Faixa em lista (e não comparação numérica): o campo 'valor' é gravado como texto "78.940,00".
        { id: 'rest_c29', nome: 'Faixa do Valor Apurado (alçada)', tipo: 'lista', obrigatorio: true, opcoes: ['Até R$ 50 mil', 'Acima de R$ 50 mil'], coresOpcoes: { 'Acima de R$ 50 mil': COR.roxo } },
      ],
    },
    {
      id: 'rest_e4', nome: 'Parecer da Procuradoria',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['rest_c1', 'rest_c12', 'rest_c16'],
      permiteMoverPara: ['rest_e5'],
      permiteCriarCards: false,
      campos: [
        { id: 'rest_c16', nome: 'Parecer', tipo: 'lista', obrigatorio: true, opcoes: ['Favorável', 'Favorável em parte', 'Desfavorável'], coresOpcoes: { 'Favorável': COR.verde, 'Favorável em parte': COR.amarelo, 'Desfavorável': COR.vermelho } },
        { id: 'rest_c17', nome: 'Procurador Responsável', tipo: 'texto', obrigatorio: false },
        { id: 'rest_c18', nome: 'Parecer (documento)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'rest_e5', nome: 'Decisão',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['rest_c1', 'rest_c19', 'rest_c20'],
      permiteMoverPara: ['rest_e6', 'rest_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'rest_c19', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Deferido', 'Deferido em parte', 'Indeferido'], coresOpcoes: CORES_DECISAO },
        { id: 'rest_c20', nome: 'Valor Deferido', tipo: 'valor', obrigatorio: false },
        { id: 'rest_c21', nome: 'Forma', tipo: 'lista', obrigatorio: false, opcoes: ['Crédito em conta', 'Compensação com débitos', 'Crédito para o exercício seguinte'] },
        { id: 'rest_c22', nome: 'Despacho', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'rest_e6', nome: 'Pagamento / Compensação',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['rest_c1', 'rest_c20', 'rest_c24'],
      permiteCriarCards: false,
      campos: [
        { id: 'rest_c23', nome: 'Ordem de Pagamento / Termo de Compensação', tipo: 'anexo', obrigatorio: false },
        { id: 'rest_c24', nome: 'Data do Crédito', tipo: 'data', obrigatorio: false },
        { id: 'rest_c25', nome: 'Contribuinte avisado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'rest_e7', nome: 'Indeferido / Prescrito',
      camposVisiveisNoCard: ['rest_c1', 'rest_c26', 'rest_c27'],
      permiteMoverPara: ['rest_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'rest_c26', nome: 'Motivo', tipo: 'lista', obrigatorio: false, opcoes: ['Prescrito (mais de 5 anos)', 'Pagamento não localizado', 'Indébito não comprovado', 'Encargo repassado a terceiro (CTN art. 166)', 'Decisão desfavorável'], coresOpcoes: { 'Prescrito (mais de 5 anos)': COR.cinza } },
        { id: 'rest_c27', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'rest_c28', nome: 'Contribuinte cientificado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Dentro dos 5 anos vai para apuração; fora, prescrito',
      etapaOrigemId: 'rest_e2',
      condicao: { campoId: 'rest_c10', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'rest_e3',
      etapaDestinoSenaoId: 'rest_e7',
    },
    {
      tipo: 'mover',
      nome: 'Acima de R$ 50 mil vai à Procuradoria (ajuste a alçada)',
      etapaOrigemId: 'rest_e3',
      condicao: { campoId: 'rest_c29', operador: 'igual', valor: 'Acima de R$ 50 mil' },
      etapaDestinoId: 'rest_e4',
      etapaDestinoSenaoId: 'rest_e5',
    },
    {
      tipo: 'mover',
      nome: 'Parecer emitido segue para decisão',
      etapaOrigemId: 'rest_e4',
      condicao: { campoId: 'rest_c16', operador: 'preenchido' },
      etapaDestinoId: 'rest_e5',
    },
    {
      tipo: 'mover',
      nome: 'Indeferido encerra; deferido vai para pagamento',
      etapaOrigemId: 'rest_e5',
      condicao: { campoId: 'rest_c19', operador: 'igual', valor: 'Indeferido' },
      etapaDestinoId: 'rest_e7',
      etapaDestinoSenaoId: 'rest_e6',
    },
  ],
  cards: [
    {
      id: 'rest_card1', titulo: 'IPTU 2026 pago em duplicidade', etapaId: 'rest_e6',
      valores: {
        rest_c1: 'IPTU 2026 pago em duplicidade',
        ...dadosContribuinte('rest', { doc: '***.341.775-**', nome: 'Renata Exemplo Freitas', whats: '(00) 99999-0701', insc: '01.04.012.0077.000', canal: 'WhatsApp', prot: '2026/REST-000144', bairro: 'Centro' }),
        rest_c2: 'Restituição', rest_c3: 'IPTU', rest_c4: 'Pagamento em duplicidade', rest_c6: 1320, rest_c10: 'Sim', rest_c12: 1351.4, rest_c29: 'Até R$ 50 mil', rest_c19: 'Deferido', rest_c20: 1351.4, rest_c21: 'Crédito em conta',
      },
    },
    {
      id: 'rest_card2', titulo: 'ISS retido em duplicidade pelo tomador', etapaId: 'rest_e4', etiquetas: ['Acima da Alçada'],
      valores: {
        rest_c1: 'ISS retido em duplicidade pelo tomador',
        ...dadosContribuinte('rest', { doc: '16.161.616/0001-30', nome: 'Engenharia Exemplo Ltda', email: 'fiscal@engenharia-exemplo.example.com', insc: '2014.00631', canal: 'Portal', prot: '2026/REST-000131', bairro: 'Bairro Industrial' }),
        rest_c2: 'Compensação com débito', rest_c3: 'ISS', rest_c4: 'ISS retido indevidamente', rest_c6: 72500, rest_c10: 'Sim', rest_c11: 'Sim', rest_c12: 78940, rest_c13: 'Sim', rest_c14: 12300, rest_c29: 'Acima de R$ 50 mil',
      },
    },
    {
      id: 'rest_card3', titulo: 'ITBI de escritura não lavrada', etapaId: 'rest_e3',
      valores: {
        rest_c1: 'ITBI de escritura não lavrada',
        ...dadosContribuinte('rest', { doc: '***.692.408-**', nome: 'Gustavo Exemplo Pires', whats: '(00) 99999-0702', insc: '03.01.004.0018.000', canal: 'Balcão', prot: '2026/REST-000150', bairro: 'Vila Nova' }),
        rest_c2: 'Restituição', rest_c3: 'ITBI', rest_c4: 'Lançamento revisto ou cancelado', rest_c6: 6800, rest_c10: 'Sim',
      },
    },
    {
      id: 'rest_card4', titulo: 'Taxa de lixo 2019 paga a maior', etapaId: 'rest_e7',
      valores: {
        rest_c1: 'Taxa de lixo 2019 paga a maior',
        ...dadosContribuinte('rest', { doc: '***.017.553-**', nome: 'Otávio Exemplo Campos', whats: '(00) 99999-0703', insc: '05.01.003.0009.000', canal: 'Telefone', prot: '2026/REST-000139', bairro: 'Morada do Sol' }),
        rest_c2: 'Restituição', rest_c3: 'Taxas', rest_c4: 'Pagamento a maior', rest_c6: 240, rest_c10: 'Não', rest_c26: 'Prescrito (mais de 5 anos)',
      },
    },
  ],
  tour: [
    '💸 Restituição e compensação com o prazo de 5 anos do CTN checado logo na entrada.',
    '1️⃣ "Pedido": tributo, motivo, data do pagamento indevido e valor.',
    '2️⃣ "Prazo e Admissibilidade": passou de 5 anos? o card vai direto para "Prescrito".',
    '3️⃣ "Apuração": valor corrigido e débitos que podem ser compensados.',
    '⚖️ Faixa "Acima de R$ 50 mil" manda o card sozinho para a Procuradoria — ajuste a alçada nas opções do campo.',
    '✅ "Decisão" e "Pagamento": crédito em conta, compensação ou crédito para o exercício seguinte.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Portal', objetivo: 'Registrar com o comprovante do pagamento indevido.' },
    { quemFaz: 'Setor de Arrecadação', objetivo: 'Conferir o prazo de 5 anos e, no ISS, quem arcou com o imposto.', automacoes: ['Já configurado: fora do prazo vai para Prescrito'] },
    { quemFaz: 'Auditor fiscal', objetivo: 'Localizar o pagamento e apurar o valor corrigido.', automacoes: ['Já configurado: acima de R$ 50 mil vai à Procuradoria'] },
    { quemFaz: 'Procuradoria', objetivo: 'Emitir parecer sobre os pedidos de maior valor.' },
    { quemFaz: 'Secretário da Fazenda', objetivo: 'Decidir e definir a forma de devolução.' },
    { quemFaz: 'Tesouraria', objetivo: 'Pagar ou registrar a compensação e avisar o contribuinte.' },
    { quemFaz: 'Setor de Arrecadação', objetivo: 'Dar ciência e informar o prazo de recurso.' },
  ],
};

// ============================================================
// A8 — Isenção e imunidade
// ============================================================
const isencaoImunidade: DefaultTemplate = {
  id: 'tpl_isencao_imunidade',
  name: 'Isenção e Imunidade',
  descricao: 'Isenção de IPTU para aposentado e PCD, imunidade de templo e de entidade sem fins lucrativos, com checklist documental, vistoria do uso do imóvel, ato declaratório e controle da renovação anual.',
  categoria: 'arrecadacao',
  icone: '🛡️',
  etiquetas: [
    { nome: 'Renovação Anual', cor: COR.azul },
    { nome: 'Imunidade Constitucional', cor: COR.roxo },
    { nome: 'Prioridade Legal (idoso/PCD)', cor: COR.rosa },
  ],
  etapas: [
    {
      id: 'isen_e1', nome: 'Requerimento',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['isen_c1', 'isen_c2', 'isen_c4'],
      permiteMoverPara: ['isen_e2'],
      campos: [
        { id: 'isen_c1', nome: 'Pedido', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('isen', 'ambas', { inscricaoObrigatoria: true }),
        { id: 'isen_c2', nome: 'Benefício', tipo: 'lista', obrigatorio: true, opcoes: ['Isenção IPTU - aposentado ou pensionista', 'Isenção IPTU - PCD ou doença grave', 'Imunidade - templo de qualquer culto', 'Imunidade - entidade de educação ou assistência sem fins lucrativos', 'Imunidade - partido político ou sindicato', 'Isenção - entidade de utilidade pública'], coresOpcoes: { 'Imunidade - templo de qualquer culto': COR.roxo, 'Imunidade - entidade de educação ou assistência sem fins lucrativos': COR.roxo, 'Imunidade - partido político ou sindicato': COR.roxo } },
        { id: 'isen_c3', nome: 'Exercício', tipo: 'numero', obrigatorio: true, valorMinimo: 2000, valorMaximo: 2100, precisao: 0 },
        { id: 'isen_c4', nome: 'Pedido de', tipo: 'lista', obrigatorio: true, opcoes: ['Concessão', 'Renovação anual'], coresOpcoes: { 'Renovação anual': COR.azul } },
        { id: 'isen_c5', nome: 'Renda Familiar (salários mínimos)', tipo: 'numero', obrigatorio: false, valorMinimo: 0, precisao: 0 },
        { id: 'isen_c6', nome: 'Único imóvel e reside nele?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'isen_c7', nome: 'Documentos', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'isen_e2', nome: 'Conferência Documental',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['isen_c1', 'isen_c2', 'isen_c9'],
      permiteMoverPara: ['isen_e3', 'isen_e4'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['isen_c9'],
      campos: [
        { id: 'isen_c8', nome: 'Checklist', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Documento do requerente', 'Comprovante de aposentadoria ou benefício', 'Comprovante de renda', 'Matrícula do imóvel', 'Laudo médico', 'Estatuto social', 'Ata de eleição da diretoria', 'Cartão CNPJ', 'Comprovante de uso do imóvel na finalidade'] },
        { id: 'isen_c9', nome: 'Documentação completa?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'isen_e3', nome: 'Pendência do Requerente',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['isen_c1', 'isen_whats', 'isen_c11'],
      permiteMoverPara: ['isen_e2', 'isen_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['isen_c12'],
      campos: [
        { id: 'isen_c10', nome: 'Exigência', tipo: 'texto_longo', obrigatorio: true },
        { id: 'isen_c11', nome: 'Prazo para Atender', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'isen_c12', nome: 'Exigência atendida?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'isen_e4', nome: 'Análise / Vistoria',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['isen_c1', 'isen_bairro', 'isen_c14'],
      permiteMoverPara: ['isen_e5', 'isen_e3'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'isen_c13', nome: 'Imóvel usado na finalidade essencial (CF art. 150, VI)?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Não se aplica'] },
        { id: 'isen_c14', nome: 'Requisitos da lei municipal atendidos?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'isen_c15', nome: 'Parecer', tipo: 'texto_longo', obrigatorio: false },
        { id: 'isen_c16', nome: 'Relatório de Vistoria', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'isen_e5', nome: 'Decisão',
      tempoMaximoHoras: 120,
      camposVisiveisNoCard: ['isen_c1', 'isen_c17', 'isen_c18'],
      permiteMoverPara: ['isen_e6', 'isen_e7'],
      permiteCriarCards: false,
      campos: [
        { id: 'isen_c17', nome: 'Decisão', tipo: 'lista', obrigatorio: true, opcoes: ['Deferido', 'Deferido em parte', 'Indeferido'], coresOpcoes: CORES_DECISAO },
        { id: 'isen_c18', nome: 'Válido até (renovação)', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'isen_c19', nome: 'Ato Declaratório', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'isen_e6', nome: 'Benefício Ativo',
      camposVisiveisNoCard: ['isen_c1', 'isen_insc', 'isen_c18'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'isen_c20', nome: 'Lançamento ajustado no sistema?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'isen_c21', nome: 'Aviso de renovação enviado?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'isen_e7', nome: 'Indeferido',
      camposVisiveisNoCard: ['isen_c1', 'isen_c22', 'isen_c23'],
      permiteMoverPara: ['isen_e2'],
      permiteCriarCards: false,
      campos: [
        { id: 'isen_c22', nome: 'Motivo', tipo: 'lista', obrigatorio: false, opcoes: ['Renda acima do limite', 'Possui mais de um imóvel', 'Imóvel fora da finalidade essencial', 'Documentação não apresentada', 'Entidade com fins lucrativos'] },
        { id: 'isen_c23', nome: 'Prazo para Recurso', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'exibir_ocultar',
      nome: 'Renda e único imóvel só para aposentado',
      etapaOrigemId: 'isen_e1',
      condicao: { campoId: 'isen_c2', operador: 'contem', valor: 'aposentado' },
      camposAfetadosIds: ['isen_c5', 'isen_c6'],
    },
    {
      tipo: 'mover',
      nome: 'Documentação completa vai para análise; incompleta volta ao requerente',
      etapaOrigemId: 'isen_e2',
      condicao: { campoId: 'isen_c9', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'isen_e4',
      etapaDestinoSenaoId: 'isen_e3',
    },
    {
      tipo: 'mover',
      nome: 'Exigência atendida volta para conferência',
      etapaOrigemId: 'isen_e3',
      condicao: { campoId: 'isen_c12', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'isen_e2',
    },
    {
      tipo: 'mover',
      nome: 'Análise concluída segue para decisão',
      etapaOrigemId: 'isen_e4',
      condicao: { campoId: 'isen_c14', operador: 'preenchido' },
      etapaDestinoId: 'isen_e5',
    },
    {
      tipo: 'mover',
      nome: 'Indeferido encerra; deferido ativa o benefício',
      etapaOrigemId: 'isen_e5',
      condicao: { campoId: 'isen_c17', operador: 'igual', valor: 'Indeferido' },
      etapaDestinoId: 'isen_e7',
      etapaDestinoSenaoId: 'isen_e6',
    },
  ],
  cards: [
    {
      id: 'isen_card1', titulo: 'Isenção de IPTU - aposentada', etapaId: 'isen_e4', etiquetas: ['Prioridade Legal (idoso/PCD)'],
      valores: {
        isen_c1: 'Isenção de IPTU - aposentada',
        ...dadosContribuinte('isen', { doc: '***.226.391-**', nome: 'Aparecida Exemplo Rocha', whats: '(00) 99999-0801', insc: '03.05.021.0033.000', canal: 'Balcão', prot: '2026/ISEN-000520', bairro: 'Vila Nova' }),
        isen_c2: 'Isenção IPTU - aposentado ou pensionista', isen_c3: 2027, isen_c4: 'Concessão', isen_c5: 2, isen_c6: 'Sim', isen_c9: 'Sim',
      },
    },
    {
      id: 'isen_card2', titulo: 'Imunidade - Igreja Exemplo', etapaId: 'isen_e6', etiquetas: ['Imunidade Constitucional', 'Renovação Anual'],
      valores: {
        isen_c1: 'Imunidade - Igreja Exemplo',
        ...dadosContribuinte('isen', { doc: '17.171.717/0001-40', nome: 'Igreja Exemplo da Comunidade', email: 'secretaria@igreja-exemplo.example.com', insc: '02.03.009.0150.000', canal: 'Portal', prot: '2026/ISEN-000488', bairro: 'Jardim das Flores' }),
        isen_c2: 'Imunidade - templo de qualquer culto', isen_c3: 2026, isen_c4: 'Renovação anual', isen_c13: 'Sim', isen_c14: 'Sim', isen_c17: 'Deferido', isen_c20: 'Sim',
      },
    },
    {
      id: 'isen_card3', titulo: 'Imunidade - Associação Exemplo de Apoio', etapaId: 'isen_e3',
      valores: {
        isen_c1: 'Imunidade - Associação Exemplo de Apoio',
        ...dadosContribuinte('isen', { doc: '18.181.818/0001-50', nome: 'Associação Exemplo de Apoio ao Idoso', whats: '(00) 99999-0802', insc: '01.06.014.0200.000', canal: 'WhatsApp', prot: '2026/ISEN-000531', bairro: 'Centro' }),
        isen_c2: 'Imunidade - entidade de educação ou assistência sem fins lucrativos', isen_c3: 2027, isen_c4: 'Concessão', isen_c9: 'Não', isen_c8: ['Cartão CNPJ', 'Estatuto social'],
        isen_c10: 'Enviar ata de eleição da diretoria atual e demonstrativo contábil do último exercício.',
      },
    },
    {
      id: 'isen_card4', titulo: 'Isenção de IPTU - PCD', etapaId: 'isen_e1', etiquetas: ['Prioridade Legal (idoso/PCD)'],
      valores: {
        isen_c1: 'Isenção de IPTU - PCD',
        ...dadosContribuinte('isen', { doc: '***.884.725-**', nome: 'Lucas Exemplo Ferraz', whats: '(00) 99999-0803', insc: '04.02.010.0062.000', canal: 'Telefone', prot: '2026/ISEN-000540', bairro: 'Morada do Sol' }),
        isen_c2: 'Isenção IPTU - PCD ou doença grave', isen_c3: 2027, isen_c4: 'Concessão',
      },
    },
  ],
  tour: [
    '🛡️ Isenções e imunidades com checklist, vistoria e controle da renovação anual.',
    '1️⃣ "Requerimento": benefício e exercício; renda e único imóvel aparecem só para aposentado.',
    '2️⃣ "Conferência Documental": faltou papel? o card volta ao requerente com a exigência.',
    '3️⃣ "Análise / Vistoria": confirma se o imóvel é usado na finalidade essencial — base da imunidade.',
    '4️⃣ "Decisão": ato declaratório e data de validade do benefício.',
    '🔁 "Benefício Ativo": ordenado pelo vencimento — o próximo a renovar fica no topo.',
    '📌 Na renovação, crie um card novo com "Pedido de: Renovação anual".',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento / Portal', objetivo: 'Registrar o benefício certo e o exercício pedido.' },
    { quemFaz: 'Setor de Cadastro / Tributos', objetivo: 'Conferir o checklist antes de mandar para análise.', automacoes: ['Já configurado: documentação incompleta volta ao requerente'] },
    { quemFaz: 'Atendimento', objetivo: 'Cobrar a exigência e controlar o prazo.' },
    { quemFaz: 'Fiscal de tributos', objetivo: 'Vistoriar o uso do imóvel e checar os requisitos da lei municipal.' },
    { quemFaz: 'Secretário da Fazenda', objetivo: 'Decidir e assinar o ato declaratório com validade definida.' },
    { quemFaz: 'Setor de Tributos', objetivo: 'Ajustar o lançamento e avisar antes do vencimento da renovação.' },
    { quemFaz: 'Setor de Tributos', objetivo: 'Informar o motivo e o prazo de recurso.' },
  ],
};

// ============================================================
// A9 — Alvará de funcionamento integrado
// ============================================================
const alvaraIntegrado: DefaultTemplate = {
  id: 'tpl_alvara_funcionamento',
  name: 'Alvará de Funcionamento Integrado',
  descricao: 'Viabilidade de endereço, classificação de risco e as licenças de Vigilância Sanitária, Bombeiros e Meio Ambiente em sequência, até a emissão do alvará. Baixo e médio risco saem direto; alto risco passa por cada órgão.',
  categoria: 'arrecadacao',
  icone: '🏢',
  etiquetas: [
    { nome: 'Baixo Risco', cor: COR.verde },
    { nome: 'Alto Risco', cor: COR.vermelho },
    { nome: 'Licença Provisória', cor: COR.amarelo },
    { nome: 'Alimentos', cor: COR.ciano },
  ],
  etapas: [
    {
      id: 'alv_e1', nome: 'Viabilidade',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['alv_c1', 'alv_c2', 'alv_bairro'],
      permiteMoverPara: ['alv_e2', 'alv_e7'],
      limparCamposAoRetornar: ['alv_c5'],
      campos: [
        { id: 'alv_c1', nome: 'Estabelecimento', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('alv', 'mobiliaria'),
        { id: 'alv_c2', nome: 'CNAE(s)', tipo: 'texto', obrigatorio: true },
        { id: 'alv_c3', nome: 'Endereço do Estabelecimento', tipo: 'texto', obrigatorio: true },
        { id: 'alv_c4', nome: 'Área Utilizada (m²)', tipo: 'numero', obrigatorio: false },
        { id: 'alv_c5', nome: 'Zoneamento permite a atividade?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'alv_c6', nome: 'Consulta de Viabilidade (REDESIM)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'alv_e2', nome: 'Classificação de Risco',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['alv_c1', 'alv_c7', 'alv_c2'],
      permiteMoverPara: ['alv_e3', 'alv_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'alv_c7', nome: 'Grau de Risco', tipo: 'lista', obrigatorio: true, opcoes: ['Baixo risco - dispensa de licença', 'Médio risco - licença automática', 'Alto risco - análise prévia'], coresOpcoes: { 'Baixo risco - dispensa de licença': COR.verde, 'Médio risco - licença automática': COR.amarelo, 'Alto risco - análise prévia': COR.vermelho } },
        { id: 'alv_c8', nome: 'Base legal', tipo: 'texto_informativo', obrigatorio: false, textoInformativo: 'Lei 13.874/2019 e resoluções do CGSIM: baixo risco dispensa ato público de liberação; médio risco recebe licença automática com fiscalização posterior; alto risco passa pelas vistorias antes da emissão.' },
        { id: 'alv_c9', nome: 'Órgãos que precisam licenciar', tipo: 'checkbox_multi', obrigatorio: false, opcoes: ['Vigilância Sanitária', 'Corpo de Bombeiros', 'Meio Ambiente'] },
      ],
    },
    {
      id: 'alv_e3', nome: 'Vigilância Sanitária',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['alv_c1', 'alv_c10', 'alv_c12'],
      permiteMoverPara: ['alv_e4', 'alv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'alv_c10', nome: 'Parecer da VISA', tipo: 'lista', obrigatorio: true, opcoes: ['Aprovado', 'Não se aplica', 'Reprovado'], coresOpcoes: { 'Aprovado': COR.verde, 'Não se aplica': COR.cinza, 'Reprovado': COR.vermelho } },
        { id: 'alv_c11', nome: 'Nº da Licença Sanitária', tipo: 'texto', obrigatorio: false },
        { id: 'alv_c12', nome: 'Validade da Licença Sanitária', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'alv_c13', nome: 'Relatório de Inspeção', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'alv_e4', nome: 'Bombeiros',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['alv_c1', 'alv_c14', 'alv_c16'],
      permiteMoverPara: ['alv_e5', 'alv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'alv_c14', nome: 'Parecer dos Bombeiros', tipo: 'lista', obrigatorio: true, opcoes: ['Aprovado', 'Não se aplica', 'Reprovado'], coresOpcoes: { 'Aprovado': COR.verde, 'Não se aplica': COR.cinza, 'Reprovado': COR.vermelho } },
        { id: 'alv_c15', nome: 'Nº do AVCB / CLCB', tipo: 'texto', obrigatorio: false },
        { id: 'alv_c16', nome: 'Validade do AVCB', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'alv_c17', nome: 'Certificado', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'alv_e5', nome: 'Licença Ambiental',
      tempoMaximoHoras: 360,
      camposVisiveisNoCard: ['alv_c1', 'alv_c18', 'alv_c19'],
      permiteMoverPara: ['alv_e6', 'alv_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      campos: [
        { id: 'alv_c18', nome: 'Parecer Ambiental', tipo: 'lista', obrigatorio: true, opcoes: ['Aprovado', 'Não se aplica', 'Reprovado'], coresOpcoes: { 'Aprovado': COR.verde, 'Não se aplica': COR.cinza, 'Reprovado': COR.vermelho } },
        { id: 'alv_c19', nome: 'Tipo de Licença', tipo: 'lista', obrigatorio: false, opcoes: ['Dispensa de licenciamento', 'Licença simplificada', 'Licença de operação'] },
        { id: 'alv_c20', nome: 'Validade da Licença Ambiental', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'alv_c21', nome: 'Licença', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'alv_e6', nome: 'Emissão do Alvará',
      tempoMaximoHoras: 48,
      camposVisiveisNoCard: ['alv_c1', 'alv_c23', 'alv_c24'],
      permiteCriarCards: false,
      campos: [
        { id: 'alv_c22', nome: 'Nº do Alvará', tipo: 'texto', obrigatorio: false },
        { id: 'alv_c23', nome: 'Tipo de Alvará', tipo: 'lista', obrigatorio: false, opcoes: ['Definitivo', 'Provisório', 'Dispensa de alvará (baixo risco)'], coresOpcoes: { 'Definitivo': COR.verde, 'Provisório': COR.amarelo, 'Dispensa de alvará (baixo risco)': COR.verde } },
        { id: 'alv_c24', nome: 'Validade do Alvará', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'alv_c25', nome: 'Taxa de licença paga?', tipo: 'lista', obrigatorio: false, opcoes: ['Sim', 'Não', 'Isento'] },
        { id: 'alv_c26', nome: 'Alvará (PDF)', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'alv_e7', nome: 'Exigência / Indeferido',
      camposVisiveisNoCard: ['alv_c1', 'alv_c27', 'alv_c29'],
      permiteMoverPara: ['alv_e1', 'alv_e3', 'alv_e4', 'alv_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      campos: [
        { id: 'alv_c27', nome: 'Órgão', tipo: 'lista', obrigatorio: false, opcoes: ['Urbanismo (zoneamento)', 'Vigilância Sanitária', 'Corpo de Bombeiros', 'Meio Ambiente', 'Fazenda'] },
        { id: 'alv_c28', nome: 'Exigência / Motivo', tipo: 'texto_longo', obrigatorio: true },
        { id: 'alv_c29', nome: 'Prazo para Cumprir', tipo: 'data_vencimento', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Endereço viável segue para classificação de risco',
      etapaOrigemId: 'alv_e1',
      condicao: { campoId: 'alv_c5', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'alv_e2',
      etapaDestinoSenaoId: 'alv_e7',
    },
    {
      tipo: 'mover',
      nome: 'Alto risco passa pelos órgãos; baixo e médio vão direto para emissão',
      etapaOrigemId: 'alv_e2',
      condicao: { campoId: 'alv_c7', operador: 'igual', valor: 'Alto risco - análise prévia' },
      etapaDestinoId: 'alv_e3',
      etapaDestinoSenaoId: 'alv_e6',
    },
    {
      tipo: 'mover',
      nome: 'VISA reprovou abre exigência; senão segue para Bombeiros',
      etapaOrigemId: 'alv_e3',
      condicao: { campoId: 'alv_c10', operador: 'igual', valor: 'Reprovado' },
      etapaDestinoId: 'alv_e7',
      etapaDestinoSenaoId: 'alv_e4',
    },
    {
      tipo: 'mover',
      nome: 'Bombeiros reprovou abre exigência; senão segue para Ambiental',
      etapaOrigemId: 'alv_e4',
      condicao: { campoId: 'alv_c14', operador: 'igual', valor: 'Reprovado' },
      etapaDestinoId: 'alv_e7',
      etapaDestinoSenaoId: 'alv_e5',
    },
    {
      tipo: 'mover',
      nome: 'Ambiental reprovou abre exigência; senão emite o alvará',
      etapaOrigemId: 'alv_e5',
      condicao: { campoId: 'alv_c18', operador: 'igual', valor: 'Reprovado' },
      etapaDestinoId: 'alv_e7',
      etapaDestinoSenaoId: 'alv_e6',
    },
  ],
  cards: [
    {
      id: 'alv_card1', titulo: 'Restaurante Exemplo - Av. Central, 1500', etapaId: 'alv_e3', etiquetas: ['Alto Risco', 'Alimentos'],
      valores: {
        alv_c1: 'Restaurante Exemplo - Av. Central, 1500',
        ...dadosContribuinte('alv', { doc: '19.191.919/0001-60', nome: 'Restaurante Exemplo Ltda', whats: '(00) 99999-0901', canal: 'Portal', prot: '2026/ALV-000703', bairro: 'Centro' }),
        alv_c2: '5611-2/01 Restaurantes e similares', alv_c3: 'Av. Central, 1500', alv_c4: 320, alv_c5: 'Sim', alv_c7: 'Alto risco - análise prévia', alv_c9: ['Vigilância Sanitária', 'Corpo de Bombeiros'],
      },
    },
    {
      id: 'alv_card2', titulo: 'Escritório de Contabilidade Exemplo', etapaId: 'alv_e6', etiquetas: ['Baixo Risco'],
      valores: {
        alv_c1: 'Escritório de Contabilidade Exemplo',
        ...dadosContribuinte('alv', { doc: '20.202.020/0001-70', nome: 'Contabilidade Exemplo S/S', email: 'contato@contab-exemplo.example.com', insc: '2026.00930', canal: 'Portal', prot: '2026/ALV-000711', bairro: 'Jardim das Flores' }),
        alv_c2: '6920-6/01 Atividades de contabilidade', alv_c3: 'Rua das Hortênsias, 210, sala 4', alv_c5: 'Sim', alv_c7: 'Baixo risco - dispensa de licença', alv_c23: 'Dispensa de alvará (baixo risco)',
      },
    },
    {
      id: 'alv_card3', titulo: 'Oficina de Funilaria Exemplo', etapaId: 'alv_e7',
      valores: {
        alv_c1: 'Oficina de Funilaria Exemplo',
        ...dadosContribuinte('alv', { doc: '21.212.121/0001-80', nome: 'Funilaria Exemplo ME', whats: '(00) 99999-0902', canal: 'Balcão', prot: '2026/ALV-000698', bairro: 'Vila Nova' }),
        alv_c2: '4520-0/02 Lanternagem ou funilaria e pintura', alv_c3: 'Rua Projetada 7, 44', alv_c5: 'Não', alv_c27: 'Urbanismo (zoneamento)',
        alv_c28: 'Zona residencial não admite funilaria e pintura. Avaliar endereço no Bairro Industrial.',
      },
    },
    {
      id: 'alv_card4', titulo: 'Academia Exemplo Fitness', etapaId: 'alv_e4', etiquetas: ['Alto Risco'],
      valores: {
        alv_c1: 'Academia Exemplo Fitness',
        ...dadosContribuinte('alv', { doc: '23.232.323/0001-90', nome: 'Academia Exemplo Ltda', whats: '(00) 99999-0903', canal: 'WhatsApp', prot: '2026/ALV-000690', bairro: 'Morada do Sol' }),
        alv_c2: '9313-1/00 Atividades de condicionamento físico', alv_c3: 'Av. das Nações, 800', alv_c4: 750, alv_c5: 'Sim', alv_c7: 'Alto risco - análise prévia', alv_c10: 'Não se aplica',
      },
    },
    {
      id: 'alv_card5', titulo: 'Loja de Roupas Exemplo', etapaId: 'alv_e2', etiquetas: ['Licença Provisória'],
      valores: {
        alv_c1: 'Loja de Roupas Exemplo',
        ...dadosContribuinte('alv', { doc: '24.242.424/0001-00', nome: 'Moda Exemplo ME', whats: '(00) 99999-0904', canal: 'Portal', prot: '2026/ALV-000720', bairro: 'Centro' }),
        alv_c2: '4781-4/00 Comércio varejista de vestuário', alv_c3: 'Rua XV, 300', alv_c5: 'Sim',
      },
    },
  ],
  tour: [
    '🏢 O alvará de funcionamento integrado: endereço, risco e as licenças de cada órgão numa trilha só.',
    '1️⃣ "Viabilidade": zoneamento não permite? o card já cai em exigência com a orientação.',
    '2️⃣ "Classificação de Risco": baixo e médio risco vão direto para a emissão (Lei de Liberdade Econômica).',
    '3️⃣ Alto risco passa por "Vigilância Sanitária", "Bombeiros" e "Licença Ambiental" — "Não se aplica" pula o órgão.',
    '⚠️ Qualquer reprovação abre "Exigência", com o órgão e o prazo para cumprir.',
    '✅ "Emissão do Alvará": tipo, validade e taxa de licença.',
    '📊 Validades de licença sanitária e AVCB ficam no card para avisar antes de vencer.',
  ],
  etapasDicas: [
    { quemFaz: 'Urbanismo / REDESIM', objetivo: 'Responder a viabilidade do endereço para a atividade.', automacoes: ['Já configurado: endereço inviável vai para exigência'] },
    { quemFaz: 'Sala do Empreendedor', objetivo: 'Classificar o risco pela tabela oficial.', automacoes: ['Já configurado: baixo e médio risco vão direto para emissão'] },
    { quemFaz: 'Vigilância Sanitária', objetivo: 'Inspecionar e emitir a licença sanitária.' },
    { quemFaz: 'Corpo de Bombeiros', objetivo: 'Emitir o AVCB ou CLCB.' },
    { quemFaz: 'Secretaria de Meio Ambiente', objetivo: 'Licenciar ou dispensar o licenciamento.' },
    { quemFaz: 'Setor de Alvarás / Fazenda', objetivo: 'Emitir o alvará e lançar a taxa de licença.', conexoes: ['Ligue ao "ISS: Cadastro Mobiliário"'] },
    { quemFaz: 'Sala do Empreendedor', objetivo: 'Explicar a exigência e devolver o card ao órgão quando cumprida.' },
  ],
};

// ============================================================
// B1 — Balcão do Prefeito (categoria Serviço Público)
// ============================================================
const balcaoPrefeito: DefaultTemplate = {
  id: 'tpl_balcao_prefeito',
  name: 'Balcão do Prefeito',
  descricao: 'Demandas que chegam ao gabinete — no atendimento ao público, no WhatsApp ou na rua — triadas em 1 dia, encaminhadas à secretaria certa, executadas com foto e respondidas ao cidadão, que avalia o atendimento. Não respondeu, a cobrança sobe sozinha.',
  categoria: 'publico',
  icone: '🏛️',
  etiquetas: [
    { nome: 'Pedido do Prefeito', cor: COR.roxo },
    { nome: 'Reaberta', cor: COR.amarelo },
    { nome: 'Risco à Segurança', cor: COR.vermelho },
    { nome: 'Imprensa / Redes', cor: COR.rosa },
  ],
  etapas: [
    {
      id: 'bp_e1', nome: 'Entrada',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['bp_c1', 'bp_c2', 'bp_bairro'],
      permiteMoverPara: ['bp_e2'],
      campos: [
        { id: 'bp_c1', nome: 'Demanda', tipo: 'texto', obrigatorio: true, usarComoTitulo: true },
        ...camposContribuinte('bp', 'nenhuma', { rotuloNome: 'Nome do Cidadão' }),
        { id: 'bp_c2', nome: 'Categoria', tipo: 'lista', obrigatorio: true, opcoes: ['Obras / Serviços Urbanos', 'Saúde', 'Educação', 'Assistência Social', 'Fazenda', 'Outros'], coresOpcoes: { 'Obras / Serviços Urbanos': COR.amarelo, 'Saúde': COR.vermelho, 'Educação': COR.azul, 'Assistência Social': COR.roxo, 'Fazenda': COR.verde, 'Outros': COR.cinza } },
        { id: 'bp_c3', nome: 'Localização (endereço / referência)', tipo: 'texto', obrigatorio: false },
        { id: 'bp_c4', nome: 'Relato do Cidadão', tipo: 'texto_longo', obrigatorio: true },
        { id: 'bp_c5', nome: 'Foto / Documento do Cidadão', tipo: 'anexo', obrigatorio: false },
      ],
    },
    {
      id: 'bp_e2', nome: 'Triagem do Gabinete',
      tempoMaximoHoras: 24,
      escalonamentoSla: true,
      camposVisiveisNoCard: ['bp_c1', 'bp_c2', 'bp_c7'],
      permiteMoverPara: ['bp_e3', 'bp_e7'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['bp_c6'],
      campos: [
        { id: 'bp_c6', nome: 'Secretaria Responsável', tipo: 'lista', obrigatorio: true, opcoes: ['Obras e Serviços Urbanos', 'Saúde', 'Educação', 'Assistência Social', 'Fazenda', 'Administração', 'Meio Ambiente', 'Outra'] },
        { id: 'bp_c7', nome: 'Prioridade', tipo: 'lista', obrigatorio: false, opcoes: ['Baixa', 'Média', 'Alta', 'Urgente'], coresOpcoes: CORES_PRIORIDADE },
        { id: 'bp_c8', nome: 'Prazo Prometido ao Cidadão', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'bp_c9', nome: 'Orientação do Gabinete', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'bp_e3', nome: 'Encaminhada à Secretaria',
      tempoMaximoHoras: 48,
      escalonamentoSla: true,
      camposVisiveisNoCard: ['bp_c1', 'bp_c6', 'bp_c8'],
      permiteMoverPara: ['bp_e4', 'bp_e2'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['bp_c11'],
      campos: [
        { id: 'bp_c10', nome: 'Responsável na Secretaria', tipo: 'texto', obrigatorio: false },
        { id: 'bp_c11', nome: 'Secretaria assumiu a demanda?', tipo: 'lista', obrigatorio: true, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'bp_c12', nome: 'Motivo da Devolução ao Gabinete', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'bp_e4', nome: 'Em Execução',
      tempoMaximoHoras: 240,
      camposVisiveisNoCard: ['bp_c1', 'bp_c6', 'bp_c14'],
      permiteMoverPara: ['bp_e5'],
      permiteCriarCards: false,
      ordenacaoCards: 'vencimento_proximo',
      limparCamposAoRetornar: ['bp_c16'],
      campos: [
        { id: 'bp_c13', nome: 'Providência Adotada', tipo: 'texto_longo', obrigatorio: false },
        { id: 'bp_c14', nome: 'Previsão de Conclusão', tipo: 'data_vencimento', obrigatorio: false },
        { id: 'bp_c15', nome: 'Foto do Resultado', tipo: 'anexo', obrigatorio: true },
        { id: 'bp_c16', nome: 'Demanda resolvida?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'bp_e5', nome: 'Resposta ao Cidadão',
      tempoMaximoHoras: 24,
      camposVisiveisNoCard: ['bp_c1', 'bp_whats', 'bp_c18'],
      permiteMoverPara: ['bp_e6'],
      permiteCriarCards: false,
      ordenacaoCards: 'mais_antigo',
      limparCamposAoRetornar: ['bp_c20'],
      campos: [
        { id: 'bp_c17', nome: 'Resposta Enviada', tipo: 'texto_longo', obrigatorio: true },
        { id: 'bp_c18', nome: 'Canal da Resposta', tipo: 'lista', obrigatorio: false, opcoes: ['WhatsApp', 'E-mail', 'Telefone', 'Pessoalmente'] },
        { id: 'bp_c19', nome: 'Data da Resposta', tipo: 'data', obrigatorio: false },
        { id: 'bp_c20', nome: 'Resposta enviada?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
      ],
    },
    {
      id: 'bp_e6', nome: 'Avaliação',
      tempoMaximoHoras: 72,
      camposVisiveisNoCard: ['bp_c1', 'bp_c21', 'bp_c22'],
      permiteMoverPara: ['bp_e7', 'bp_e2'],
      permiteCriarCards: false,
      limparCamposAoRetornar: ['bp_c22'],
      campos: [
        { id: 'bp_c21', nome: 'Nota do Cidadão (1 a 5)', tipo: 'numero', obrigatorio: false, valorMinimo: 1, valorMaximo: 5, precisao: 0 },
        { id: 'bp_c22', nome: 'Cidadão considera resolvido?', tipo: 'lista', obrigatorio: false, opcoes: [...SIM_NAO], coresOpcoes: CORES_SIM_NAO },
        { id: 'bp_c23', nome: 'Comentário do Cidadão', tipo: 'texto_longo', obrigatorio: false },
      ],
    },
    {
      id: 'bp_e7', nome: 'Encerrada',
      camposVisiveisNoCard: ['bp_c1', 'bp_c2', 'bp_c24'],
      permiteCriarCards: false,
      campos: [
        { id: 'bp_c24', nome: 'Resultado', tipo: 'lista', obrigatorio: false, opcoes: ['Resolvida', 'Resolvida em parte', 'Não é competência do município', 'Sem retorno do cidadão'], coresOpcoes: { 'Resolvida': COR.verde, 'Resolvida em parte': COR.amarelo, 'Não é competência do município': COR.cinza } },
        { id: 'bp_c25', nome: 'Data do Encerramento', tipo: 'data', obrigatorio: false },
      ],
    },
  ],
  automacoes: [
    {
      tipo: 'mover',
      nome: 'Secretaria definida encaminha a demanda',
      etapaOrigemId: 'bp_e2',
      condicao: { campoId: 'bp_c6', operador: 'preenchido' },
      etapaDestinoId: 'bp_e3',
    },
    {
      tipo: 'mover',
      nome: 'Secretaria assumiu vai para execução; devolveu volta ao gabinete',
      etapaOrigemId: 'bp_e3',
      condicao: { campoId: 'bp_c11', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'bp_e4',
      etapaDestinoSenaoId: 'bp_e2',
    },
    {
      tipo: 'exibir_ocultar',
      nome: 'Motivo da devolução só quando a secretaria não assume',
      etapaOrigemId: 'bp_e3',
      condicao: { campoId: 'bp_c11', operador: 'igual', valor: 'Não' },
      camposAfetadosIds: ['bp_c12'],
    },
    {
      tipo: 'mover',
      nome: 'Resolvida (com foto) vai para resposta ao cidadão',
      etapaOrigemId: 'bp_e4',
      condicao: { campoId: 'bp_c16', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'bp_e5',
    },
    {
      tipo: 'mover',
      nome: 'Resposta enviada abre a avaliação',
      etapaOrigemId: 'bp_e5',
      condicao: { campoId: 'bp_c20', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'bp_e6',
    },
    {
      tipo: 'mover',
      nome: 'Cidadão satisfeito encerra',
      etapaOrigemId: 'bp_e6',
      condicao: { campoId: 'bp_c22', operador: 'igual', valor: 'Sim' },
      etapaDestinoId: 'bp_e7',
    },
    {
      tipo: 'mover',
      nome: 'Cidadão não satisfeito reabre no gabinete',
      etapaOrigemId: 'bp_e6',
      condicao: { campoId: 'bp_c22', operador: 'igual', valor: 'Não' },
      etapaDestinoId: 'bp_e2',
    },
  ],
  cards: [
    {
      id: 'bp_card1', titulo: 'Buraco na Rua das Acácias em frente à escola', etapaId: 'bp_e4', etiquetas: ['Risco à Segurança'],
      valores: {
        bp_c1: 'Buraco na Rua das Acácias em frente à escola',
        ...dadosContribuinte('bp', { doc: '***.553.170-**', nome: 'Rosana Exemplo Lopes', whats: '(00) 99999-1001', canal: 'WhatsApp', prot: '2026/GAB-001842', bairro: 'Jardim das Flores' }),
        bp_c2: 'Obras / Serviços Urbanos', bp_c3: 'Rua das Acácias, em frente à EM Exemplo', bp_c4: 'Buraco grande na faixa de pedestres, crianças desviam pela rua na saída da escola.',
        bp_c6: 'Obras e Serviços Urbanos', bp_c7: 'Urgente', bp_c11: 'Sim', bp_c10: 'Coordenação de pavimentação', bp_c13: 'Equipe de tapa-buraco programada para quinta-feira.',
      },
    },
    {
      id: 'bp_card2', titulo: 'Consulta de especialista sem data há 4 meses', etapaId: 'bp_e3', etiquetas: ['Pedido do Prefeito'],
      valores: {
        bp_c1: 'Consulta de especialista sem data há 4 meses',
        ...dadosContribuinte('bp', { doc: '***.307.926-**', nome: 'Antônio Exemplo Barros', whats: '(00) 99999-1002', canal: 'Balcão', prot: '2026/GAB-001851', bairro: 'Vila Nova' }),
        bp_c2: 'Saúde', bp_c4: 'Aguarda consulta com cardiologista desde junho; encaminhamento da UBS Vila Nova.',
        bp_c6: 'Saúde', bp_c7: 'Alta', bp_c9: 'Prefeito pediu retorno pessoal ao cidadão.',
      },
    },
    {
      id: 'bp_card3', titulo: 'Vaga em creche para filho de 2 anos', etapaId: 'bp_e2',
      valores: {
        bp_c1: 'Vaga em creche para filho de 2 anos',
        ...dadosContribuinte('bp', { doc: '***.871.443-**', nome: 'Juliana Exemplo Matos', whats: '(00) 99999-1003', email: 'juliana.exemplo@example.com', canal: 'Portal', prot: '2026/GAB-001860', bairro: 'Morada do Sol' }),
        bp_c2: 'Educação', bp_c4: 'Inscrita na fila da creche desde fevereiro, precisa voltar ao trabalho em novembro.',
      },
    },
    {
      id: 'bp_card4', titulo: 'Poste apagado na praça do bairro', etapaId: 'bp_e6',
      valores: {
        bp_c1: 'Poste apagado na praça do bairro',
        ...dadosContribuinte('bp', { doc: '***.640.218-**', nome: 'Paulo Exemplo Moreira', whats: '(00) 99999-1004', canal: 'Telefone', prot: '2026/GAB-001799', bairro: 'Bairro Industrial' }),
        bp_c2: 'Obras / Serviços Urbanos', bp_c3: 'Praça do Bairro Industrial', bp_c6: 'Obras e Serviços Urbanos', bp_c11: 'Sim', bp_c16: 'Sim',
        bp_c17: 'Lâmpada e relé trocados na terça-feira. Segue a foto do poste aceso.', bp_c18: 'WhatsApp', bp_c20: 'Sim', bp_c21: 5,
      },
    },
    {
      id: 'bp_card5', titulo: 'Dúvida sobre parcelamento do IPTU atrasado', etapaId: 'bp_e1',
      valores: {
        bp_c1: 'Dúvida sobre parcelamento do IPTU atrasado',
        ...dadosContribuinte('bp', { doc: '***.129.054-**', nome: 'Márcia Exemplo Duarte', whats: '(00) 99999-1005', canal: 'WhatsApp', prot: '2026/GAB-001866', bairro: 'Centro' }),
        bp_c2: 'Fazenda', bp_c4: 'Quer saber se pode parcelar o IPTU de 2024 e 2025 no REFIS.',
      },
    },
  ],
  tour: [
    '🏛️ O balcão do gabinete: toda demanda que chega ao Prefeito com dono, prazo e resposta.',
    '1️⃣ "Entrada": categoria, bairro e localização — dá o mapa de onde a cidade mais pede.',
    '2️⃣ "Triagem do Gabinete": escolhida a secretaria, o card vai sozinho para ela. Prazo de 1 dia.',
    '3️⃣ "Encaminhada à Secretaria": 2 dias para assumir; passou do prazo, a cobrança sobe por e-mail na hierarquia.',
    '4️⃣ "Em Execução": a demanda só fecha com foto do resultado.',
    '5️⃣ "Resposta" e "Avaliação": o cidadão recebe a resposta e dá nota; não ficou satisfeito, reabre no gabinete.',
    '📌 Etiqueta "Pedido do Prefeito" para o que ele mesmo prometeu na rua.',
  ],
  etapasDicas: [
    { quemFaz: 'Atendimento do Gabinete / WhatsApp', objetivo: 'Registrar com bairro, localização e o relato nas palavras do cidadão.' },
    { quemFaz: 'Chefia de Gabinete', objetivo: 'Triar em até 1 dia e definir a secretaria e a prioridade.', automacoes: ['Já configurado: secretaria escolhida encaminha o card', 'Escalonamento de SLA ligado: atraso sobe na hierarquia'] },
    { quemFaz: 'Secretaria responsável', objetivo: 'Assumir em até 2 dias ou devolver com motivo.', automacoes: ['Já configurado: devolvida volta para a triagem'] },
    { quemFaz: 'Equipe da secretaria', objetivo: 'Resolver e anexar a foto do resultado — sem foto não fecha.' },
    { quemFaz: 'Atendimento do Gabinete', objetivo: 'Responder o cidadão em até 1 dia depois de resolvido.' },
    { quemFaz: 'Atendimento do Gabinete', objetivo: 'Colher a nota; insatisfeito, reabre na triagem com a etiqueta "Reaberta".' },
    { quemFaz: 'Chefia de Gabinete', objetivo: 'Encerrar e acompanhar os indicadores por bairro e secretaria.', conexoes: ['Ligue à "Ouvidoria" e às "Solicitações do Cidadão (Zeladoria)"'] },
  ],
};

/**
 * O campo 'valor' é gravado pela tela como texto no formato "1.234,56" (centavos
 * sempre presentes). Nos cards de exemplo os valores estão escritos como número
 * para facilitar a leitura; aqui viram o mesmo formato que a tela grava.
 */
function comValoresEmReais(t: DefaultTemplate): DefaultTemplate {
  const camposValor = new Set<string>();
  t.etapas.forEach(e => e.campos.forEach(c => { if (c.tipo === 'valor') camposValor.add(c.id); }));
  const fmt = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return {
    ...t,
    cards: t.cards.map(card => {
      const valores: Record<string, any> = {};
      Object.keys(card.valores).forEach(k => {
        const v = card.valores[k];
        valores[k] = camposValor.has(k) && typeof v === 'number' ? fmt(v) : v;
      });
      return { ...card, valores };
    }),
  };
}

export const TEMPLATES_ARRECADACAO: DefaultTemplate[] = [
  iptuRevisao,
  itbi,
  issCadastro,
  nfseSimples,
  parcelamentoRefis,
  certidaoNegativa,
  restituicao,
  isencaoImunidade,
  alvaraIntegrado,
  balcaoPrefeito,
].map(comValoresEmReais);
